import React, { useState, useMemo } from 'react';
import { groupBlocks } from '../../services/crossCover';
import {
  Play, Square,
  CheckCircle2, AlertTriangle, ChevronRight, LayoutDashboard, Info, X, Layers, ArrowLeftRight, Trash2, User, Search, ShieldCheck, ChevronDown, ChevronUp, Plus, Lock, Unlock, CalendarOff, MoreHorizontal
} from 'lucide-react';
import { createShiftBucketer } from '../../utils/shiftBucketing';

// Component Imports
import Card from '../ui/Card';
import Button from '../ui/Button';
import Drawer from '../ui/Drawer';
import Dropdown from '../ui/Dropdown';
import PeriodNav from '../ui/PeriodNav';
import ConfirmDialog from '../ui/ConfirmDialog';
import { formatDateRange, formatCompactDateRange } from '../../utils/dateRange';
import { toRanges, formatDraftRange } from '../../services/draftDates';
import { scopeShifts } from '../../services/rosterScope';
import { isOnRotation, institutionOf } from '../../services/staffFields';
import { isLeadSlot, meetsSkillGate } from '../../services/slots';
import { idOf, samePerson } from '../../services/people';
import { onCallId, rolesOf, roleName, mayTake, roleTag, GRADE_LABELS } from '../../services/onCallRoster';
import { siteOf } from '../../services/locationSites';
import { rankOf, gradeOf } from '../../services/ranks';
import { locationName, slotLocationName } from '../../services/locations';
import { DraggableEmployee, DroppableSlot } from '../ui/DndComponents';
import { onLeave } from '../../services/leave';
import PageHeader from '../ui/PageHeader';
import ControlGroup from '../ui/ControlGroup';
import { ToggleGroup, EndGroup } from '../ui/ControlBar';

  // Weekend column tinting, matching the Final Roster views. The console always
  // spans Mon..Sun, so Sat/Sun are tinted to keep them visually distinct.
  const isWeekendDate = (d) => { const n = new Date(d).getDay(); return n === 0 || n === 6; };
  const WEEKEND_HEADER = 'bg-slate-200';
  const WEEKEND_CELL = 'bg-slate-100';

const toLocalISO = (date) => {
  const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// --- HELPER: Parse YYYY-MM-DD safely into local date ---
const parseLocalDate = (ds) => {
  if (!ds) return new Date();
  const [y, m, d] = ds.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// People are matched by staff id (services/people.js), never by name.

// --- CORE HELPER: ON-CALL VALIDATOR (Swap-Aware) ---
// Each finding carries a fixed `code` (Oct 2026) -- 'grade', 'site', 'rotation',
// 'leave', 'back_to_back', 'two_roles' -- so the audit sorts findings by it,
// never by searching the wording of `reason`, which is for people to read.
const validateOnCallAssignment = (employee, targetDateStr, targetRole, shifts, schedule, excludeShift = null) => {
  if (!employee) return { valid: true };
  const empName = employee.name;          // for messages only; identity is employee.id

  // The on-call GRADE set on the person decides which roles they may take
  // (0 consultant, 1 senior resident, 2 junior) -- the same test the solver
  // applies, against the department's own roles. Somebody with no grade takes
  // no on-call. A title decides nothing (Oct 2026: it was a fallback).
  // targetRole is the role's KEY; its name is only for the messages.
  const role = rolesOf(schedule).find(r => r.key === targetRole);
  const targetName = roleName(rolesOf(schedule), targetRole);
  const grade = gradeOf(employee);
  if (role) {
    if (grade !== role.grade) {
      const who = grade !== null ? `in the ${GRADE_LABELS[grade]} on-call pool` : 'in no on-call pool';
      return { valid: false, code: 'grade', reason: `${empName} is ${who}, not eligible for ${targetName}` };
    }
    if (!mayTake(role, grade, institutionOf(employee) || null))
      return { valid: false, code: 'site', reason: `${empName} is ${institutionOf(employee)} staff; ${targetName} is for ${role.site} staff only` };
  }

  // Rotation dates: outside their posting a person takes no on-call.
  if (!isOnRotation(employee, String(targetDateStr).slice(0, 10)))
    return { valid: false, code: 'rotation', reason: `${empName} is outside their rotation dates on ${targetDateStr}` };

  const dateObj = parseLocalDate(targetDateStr);
  const prevDateStr = toLocalISO(new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate() - 1));
  const nextDateStr = toLocalISO(new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate() + 1));

  const isUnavailable = (schedule, person, date, block) => {
    if (!schedule?.employees) return false;
    const emp = schedule.employees.find(e => samePerson(e, person));
    if (!emp) return false;
    const ds = typeof date === 'string' ? date : toLocalISO(date);
    return onLeave(emp, ds, block);
  };

  // 2. Leave Check
  if (isUnavailable(schedule, employee, dateObj, 'AM') || isUnavailable(schedule, employee, dateObj, 'PM')) {
    return { valid: false, code: 'leave', reason: `${empName} is on leave on ${targetDateStr}` };
  }

  // 3. Proximity Check (Back-to-Back)
  const isB2B = (shifts || []).some(s => {
    // IGNORE the specific slot we are moving FROM/TO (by id if available, or date+role)
    if (excludeShift) {
      if (excludeShift.id && s.id === excludeShift.id) return false;
      if (s.date === excludeShift.date && s.role === excludeShift.role) return false;
    }
    return (s.date === prevDateStr || s.date === nextDateStr) && samePerson(s.employee, employee);
  });
  
  if (isB2B) {
    if (grade === 0) {
      return { valid: true, warning: true, code: 'back_to_back', reason: `${empName} would be working back-to-back duties (Allowed for Consultants)` };
    }
    return { valid: false, code: 'back_to_back', reason: `${empName} would be working back-to-back duties` };
  }

  // 4. Collision Check (Already on duty in different role)
  const isCollision = (shifts || []).some(s => {
    if (excludeShift) {
      if (excludeShift.id && s.id === excludeShift.id) return false;
      if (s.date === excludeShift.date && s.role === excludeShift.role) return false;
    }
    return s.date === targetDateStr && s.role !== targetRole && samePerson(s.employee, employee);
  });
  if (isCollision) return { valid: false, code: 'two_roles', reason: `${empName} is already assigned to a different role on this day` };

  return { valid: true };
};

// --- SUB-COMPONENT: SAFETY BLOCK MODAL ---
const SafetyBlockModal = ({ isOpen, onClose, onConfirm, reason, isWarning }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8 text-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${isWarning ? 'bg-amber-50 text-amber-500' : 'bg-rose-50 text-rose-500'}`}>
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-black text-slate-900 mb-2 tracking-tight uppercase">
            {isWarning ? 'Safety Warning' : 'Safety Violation'}
          </h3>
          <p className="text-sm text-slate-500 font-bold leading-relaxed px-4">{reason}</p>
          <div className="mt-8 flex flex-col gap-2">
            {isWarning && (
              <Button variant="accent" onClick={onConfirm} className="w-full py-3 shadow-lg shadow-teal-100">
                Proceed Anyway
              </Button>
            )}
            <Button variant={isWarning ? 'secondary' : 'primary'} onClick={onClose} className={`w-full py-3 shadow-none ${!isWarning ? 'bg-rose-600 hover:bg-rose-700' : ''}`}>
              {isWarning ? 'Cancel' : 'Dismiss Warning'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: AUDIT MODAL ---
// A small collapsible row shared by both sections below: a summary header
// (label, count, penalty) that expands to the individual instances. Keeping
// the raw list flat is what made this modal a long, hard-to-scan scroll --
// grouping by issue TYPE gives an overview first, detail on demand.
const AuditGroup = ({ groupKey, expanded, onToggle, accent, icon: Icon, title, meta, badge, children }) => {
  const isOpen = !!expanded;
  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all ${accent}`}>
      <button
        type="button"
        onClick={() => onToggle(groupKey)}
        className="w-full flex items-center gap-3 p-4 text-left"
        style={{ background: 'transparent', boxShadow: 'none' }}
      >
        <div className="p-2 rounded-lg bg-white shadow-sm flex-shrink-0">{Icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-700">{title}</p>
          {meta && <p className="text-[10px] text-slate-500 mt-0.5">{meta}</p>}
        </div>
        {badge}
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 space-y-2">{children}</div>}
    </div>
  );
};

// "alternating_weekend_off" -> "Alternating Weekend Off" -- fallback when a
// real display name isn't available (constraintLabels hasn't loaded, or the
// id is unrecognised), so the modal never shows a raw framework identifier.
const prettifyConstraintId = (id) =>
  String(id || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Per-instance descriptions are written by the constraint that raised them
// (e.g. "Rohainiza on designated weekend day off (2026-09-06, week 2974)").
// The internal week-index is an accounting detail with no meaning to a
// reader, so it's stripped here -- display only, the raw string is untouched
// everywhere else (exports, backend).
const cleanTermDescription = (desc) =>
  String(desc || '').replace(/,\s*week\s+\d+\)/i, ')');

/**
 * The per-employee count table for an aggregate constraint, or null.
 *
 * A `breakdown` describes the WHOLE matched group, so any term carrying one is
 * the table to show. This was previously keyed on the constraint emitting
 * exactly one term -- true until workload_balance gained a spread cap, which
 * adds a second term for the overshoot. The table then vanished from the audit
 * modal in favour of the generic per-instance list, with no error: a display
 * regression that only shows up as "where did the table go".
 *
 * Exported so that stays pinned by a test rather than by reading JSX.
 */
export const pickBreakdown = (terms) =>
  (terms || []).map((t) => t && t.breakdown)
    .find((b) => Array.isArray(b) && b.length > 0) || null;

const AuditModal = ({ isOpen, onClose, auditLogs, mode, rosterCheck, rosterChecking, constraintLabels }) => {
  const [expandedGroups, setExpandedGroups] = React.useState(() => new Set());
  const toggleGroup = (key) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  if (!isOpen) return null;
  const isClinical = mode === 'clinical';
  // The always-on client heuristics (unfilled slots + leave collisions).
  const heuristicLogs = auditLogs || [];
  // The on-demand real constraint-framework result (clinical only; the on-call
  // roster is validated separately by its own solver).
  const showConstraintSection = isClinical;
  // rosterCheck is populated exclusively by the clinical-only Check Conflicts
  // flow and is never cleared on a mode switch, so every read of it below is
  // gated on isClinical -- otherwise a stale clinical result (violations,
  // feasibility, "checked" status) could flash as an on-call verdict.
  const violations = isClinical ? (rosterCheck?.violations || []) : [];
  const feasible = rosterCheck?.feasible;
  const checked = isClinical && !!rosterCheck && !rosterChecking;
  const hasHardBreak = isClinical && checked && feasible === false;

  // Group heuristics by type ("Mandatory Gap", "Leave Conflict", ...),
  // preserving first-seen order, worst severity leading each group's badge.
  const heuristicGroups = (() => {
    const order = [];
    const byType = new Map();
    heuristicLogs.forEach((log) => {
      if (!byType.has(log.type)) { byType.set(log.type, []); order.push(log.type); }
      byType.get(log.type).push(log);
    });
    return order.map((type) => {
      const items = byType.get(type);
      const severity = items.some((l) => l.severity === 'Critical') ? 'Critical'
        : items.some((l) => l.severity === 'High') ? 'High' : 'Low';
      return { key: `h:${type}`, type, items, severity };
    });
  })();

  // Worst (highest weighted_penalty) first -- the low-penalty trade-offs stay
  // in the list, just naturally sink toward the bottom instead of competing
  // for attention with the issue that actually matters.
  const sortedViolations = [...violations].sort(
    (a, b) => (b.weighted_penalty || 0) - (a.weighted_penalty || 0)
  );

  const totalIssueTypes = heuristicGroups.length + sortedViolations.length + (hasHardBreak ? 1 : 0);
  const expandAllKeys = () => {
    const keys = [
      ...heuristicGroups.map((g) => g.key),
      ...sortedViolations.map((v) => `v:${v.id}`),
    ];
    setExpandedGroups(new Set(keys));
  };
  const collapseAll = () => setExpandedGroups(new Set());

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8">
          <div className="flex justify-between items-center mb-5">
            <div className="flex items-center gap-3"><div className="p-2 bg-slate-50 rounded-xl border border-slate-100 text-slate-500 shadow-sm">{isClinical ? <Layers className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}</div><div><h3 className="text-xl font-bold text-slate-900 tracking-tight">{isClinical ? 'Roster Audit' : 'Optimization Conflict Audit'}</h3><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{isClinical ? 'Fulfilment gaps & rule validation' : 'Trade-offs and Constraint Violations'}</p></div></div>
            <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
          </div>

          {/* VERDICT — the headline answer, so nobody has to tally two lists
              to know whether this roster is okay. */}
          {rosterChecking ? (
            <div className="mb-5 p-4 rounded-xl border-2 bg-slate-50 border-slate-100 flex items-center gap-3">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin flex-shrink-0" />
              <p className="text-xs font-bold text-slate-500">Running full constraint scan…</p>
            </div>
          ) : hasHardBreak ? (
            <div className="mb-5 p-4 rounded-xl border-2 bg-rose-50 border-rose-200 flex gap-3 items-start">
              <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div><p className="text-sm font-black text-rose-700">Hard rule broken — this roster cannot be published as-is</p><p className="text-[11px] text-rose-600 mt-0.5 leading-relaxed">{rosterCheck?.infeasible_reason || 'The roster breaks a hard constraint.'}</p></div>
            </div>
          ) : totalIssueTypes === 0 ? (
            <div className="mb-5 p-4 rounded-xl border-2 bg-emerald-50 border-emerald-200 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <p className="text-xs font-black text-emerald-700">{checked ? 'This roster looks good — no gaps, leave conflicts, or rule violations.' : 'No gaps or leave conflicts detected.'}</p>
            </div>
          ) : (
            <div className="mb-5 p-4 rounded-xl border-2 bg-amber-50 border-amber-200 flex items-center gap-3">
              <Info className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-xs font-black text-amber-700">{totalIssueTypes} issue type{totalIssueTypes === 1 ? '' : 's'} found — see below for who and where.</p>
            </div>
          )}
          {!checked && !rosterChecking && showConstraintSection && (
            <p className="text-[10px] text-slate-400 -mt-3 mb-5 ml-1">Rule validation hasn't run yet — press <span className="font-bold text-slate-500">Check conflicts</span> for the full picture.</p>
          )}

          <div className="flex items-center justify-end gap-3 mb-2">
            <button onClick={expandAllKeys} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-widest" style={{ background: 'transparent', boxShadow: 'none' }}>Expand all</button>
            <span className="text-slate-200">·</span>
            <button onClick={collapseAll} className="text-[10px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-widest" style={{ background: 'transparent', boxShadow: 'none' }}>Collapse all</button>
          </div>

          <div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar space-y-5">

            {/* SECTION 1 — always-on fulfilment / leave heuristics, grouped by type */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Unfilled slots & leave</span>
                <span className="text-[9px] text-slate-300 font-bold">live</span>
              </div>
              {heuristicGroups.length === 0 ? (
                <div className="py-6 text-center space-y-2"><div className="w-9 h-9 bg-emerald-50 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-4 h-4 text-emerald-500" /></div><p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">No gaps or leave conflicts</p></div>
              ) : (
                heuristicGroups.map((g) => (
                  <AuditGroup
                    key={g.key}
                    groupKey={g.key}
                    expanded={expandedGroups.has(g.key)}
                    onToggle={toggleGroup}
                    accent={g.severity === 'Critical' ? 'bg-rose-50 border-rose-100' : g.severity === 'High' ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'}
                    icon={g.severity === 'Critical' ? <AlertTriangle className="w-4 h-4 text-rose-500" /> : <Info className="w-4 h-4 text-amber-500" />}
                    title={g.type}
                    meta={`${g.items.length} instance${g.items.length === 1 ? '' : 's'}`}
                    badge={<span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full flex-shrink-0 ${g.severity === 'Critical' ? 'bg-rose-200 text-rose-700' : g.severity === 'High' ? 'bg-amber-200 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>{g.severity}</span>}
                  >
                    {g.items.map((log, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-white border border-slate-100">
                        <p className="text-xs font-bold text-slate-700">{log.staff} • {log.date}</p>
                        <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{log.desc}</p>
                      </div>
                    ))}
                  </AuditGroup>
                ))
              )}
            </div>

            {/* SECTION 2 — on-demand REAL constraint framework check, grouped by
                constraint and sorted worst-first; each group expands to the
                exact person/date instances behind it. */}
            {showConstraintSection && (
              <div className="space-y-2 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rule validation</span>
                  <span className="text-[9px] text-slate-300 font-bold">{rosterChecking ? 'running…' : checked ? 'last checked just now' : 'not run yet'}</span>
                  {/* Which window this verdict is ABOUT. Without it a result
                      left over from another scope reads as the current one. */}
                  {checked && rosterCheck?.window?.start && (
                    <span className="text-[9px] font-bold text-slate-400 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-full">
                      {rosterCheck.window.start} → {rosterCheck.window.end}
                    </span>
                  )}
                </div>

                {/* RULES LOOKING AT NOTHING.
                    A selector naming a shift type this department does not have
                    matches nothing, costs nothing, and so never appears in the
                    violations list -- indistinguishable from a rule that is
                    simply satisfied. Four rules were inert that way for the
                    whole Inpatient UAT. Surfaced first, and in red, because a
                    rule that is not running is a bigger problem than one that
                    is running and costing points. */}
                {!rosterChecking && checked && (rosterCheck?.coverage_warnings || []).length > 0 && (
                  <div className="p-3 rounded-xl border-2 bg-rose-50 border-rose-100 space-y-1">
                    <p className="text-[10px] font-black text-rose-700 uppercase tracking-widest">
                      {rosterCheck.inert_count > 0
                        ? `${rosterCheck.inert_count} rule${rosterCheck.inert_count === 1 ? '' : 's'} not running`
                        : 'Rule settings do not match this roster'}
                    </p>
                    {rosterCheck.coverage_warnings.map((c) => (
                      <p key={c.id} className="text-[11px] text-rose-600 leading-relaxed">
                        <span className="font-bold">
                          {(constraintLabels && constraintLabels[c.id]) || prettifyConstraintId(c.id)}
                        </span>{' '}{c.reason}
                      </p>
                    ))}
                  </div>
                )}

                {/* LOCKS OVERRIDING A RULE (Oct 2026). A lock wins over the rules,
                    as on main: a locked person keeps the slot even if, say,
                    leave was booked afterwards. Listed so it can be fixed --
                    unlock, or move the leave -- rather than staying silent. */}
                {!rosterChecking && checked && (rosterCheck?.lock_overrides || []).length > 0 && (
                  <div className="p-3 rounded-xl border-2 bg-amber-50 border-amber-200 space-y-1">
                    <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest">
                      {rosterCheck.lock_overrides.length} lock{rosterCheck.lock_overrides.length === 1 ? '' : 's'} overriding a rule
                    </p>
                    {rosterCheck.lock_overrides.map((o) => (
                      <p key={`${o.shift_id}|${o.rule}`} className="text-[11px] text-amber-800 leading-relaxed">
                        <span className="font-bold">{o.staff}</span> is locked on {o.slot}, which breaks{' '}
                        <span className="font-bold">{o.rule}</span>.
                      </p>
                    ))}
                  </div>
                )}

                {!rosterChecking && checked && rosterCheck?.error && (
                  <div className="p-4 rounded-xl border-2 bg-rose-50 border-rose-100"><p className="text-xs font-bold text-rose-600">Validation failed to run. Check the connection and try again.</p></div>
                )}

                {!rosterChecking && checked && !rosterCheck?.error && (
                  sortedViolations.length === 0 ? (
                    <p className="text-[11px] text-slate-400 text-center py-3">No trade-off penalties — every soft rule is fully satisfied.</p>
                  ) : (
                    sortedViolations.map((v) => {
                      const key = `v:${v.id}`;
                      const terms = v.terms || [];
                      const label = (constraintLabels && constraintLabels[v.id]) || prettifyConstraintId(v.id);
                      const breakdown = pickBreakdown(terms);
                      // Descriptions still worth showing alongside the table --
                      // "spread over the cap of 1" says something the counts do
                      // not. Only listed when there is more than one term.
                      const extraTerms = breakdown && terms.length > 1 ? terms : [];
                      return (
                        <AuditGroup
                          key={key}
                          groupKey={key}
                          expanded={expandedGroups.has(key)}
                          onToggle={toggleGroup}
                          accent="bg-amber-50 border-amber-100"
                          icon={<Info className="w-4 h-4 text-amber-500" />}
                          title={label}
                          meta={breakdown ? `${breakdown.length} staff — highest to lowest` : terms.length > 0 ? `${terms.length} instance${terms.length === 1 ? '' : 's'}` : v.description}
                          badge={<span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full flex-shrink-0 tabular-nums">{Math.round(v.weighted_penalty || 0).toLocaleString()} pts</span>}
                        >
                          {breakdown ? (
                            // An aggregate gap term has no single "instance" to
                            // point at -- show every matched employee's actual
                            // count instead, worst (highest) first, so the
                            // spread the penalty is about is visible directly.
                            <>
                            {extraTerms.length > 0 && (
                              <div className="mb-2 space-y-1">
                                {extraTerms.map((t, idx) => (
                                  <p key={idx} className="text-[11px] text-slate-500 leading-relaxed">
                                    {cleanTermDescription(t.description)}
                                    {t.weighted != null && (
                                      <span className="text-[9px] font-bold text-slate-400 tabular-nums ml-2">
                                        {Math.round(t.weighted).toLocaleString()}
                                      </span>
                                    )}
                                  </p>
                                ))}
                              </div>
                            )}
                            <div className="rounded-lg bg-white border border-amber-100 divide-y divide-amber-50 overflow-hidden">
                              {breakdown.map((row, i) => (
                                <div key={row.label} className={`flex items-center justify-between px-3 py-2 ${i === 0 ? 'bg-amber-50/60' : ''}`}>
                                  <span className={`text-[11px] ${i === 0 ? 'font-bold text-slate-700' : 'text-slate-600'}`}>{row.label}</span>
                                  <span className={`text-[11px] tabular-nums ${i === 0 ? 'font-bold text-amber-700' : 'text-slate-400'}`}>{row.value}</span>
                                </div>
                              ))}
                            </div>
                            </>
                          ) : terms.length > 0 ? terms.map((t, idx) => (
                            <div key={idx} className="p-3 rounded-lg bg-white border border-amber-100 flex items-center justify-between gap-3">
                              <p className="text-[11px] text-slate-600 leading-relaxed">{cleanTermDescription(t.description)}</p>
                              {t.weighted != null && <span className="text-[9px] font-bold text-slate-400 tabular-nums flex-shrink-0">{Math.round(t.weighted).toLocaleString()}</span>}
                            </div>
                          )) : (
                            <p className="text-[11px] text-slate-500 leading-relaxed">{v.description}</p>
                          )}
                        </AuditGroup>
                      );
                    })
                  )
                )}

                {/* RULES THAT WERE CHECKED AND COST NOTHING.
                    Listed quietly rather than omitted. Absence used to mean
                    three different things -- satisfied, silently inert, or a
                    stale result -- and they all rendered the same way: nothing.
                    Naming what passed is what makes a missing rule meaningful. */}
                {!rosterChecking && checked && !rosterCheck?.error
                  && (rosterCheck?.satisfied || []).length > 0 && (
                  <div className="pt-2">
                    <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1.5">
                      Checked · no penalty ({rosterCheck.satisfied.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {rosterCheck.satisfied.map((c) => (
                        <span key={c.id}
                          className="text-[10px] text-slate-400 bg-slate-50 border border-slate-100 px-2 py-1 rounded-full">
                          {(constraintLabels && constraintLabels[c.id]) || prettifyConstraintId(c.id)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mt-8 pt-6 border-t border-slate-100"><Button variant="secondary" onClick={onClose} className="w-full py-3 shadow-none">Close Audit Report</Button></div>
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: ON-CALL SHIFT DRAWER ---
const OnCallShiftDrawer = ({ 
  isOpen, onClose, shift, employees, onCallSchedule, onAssign, onUnassign, onPrepareSwap,
  currentMonth, schedule, isEmployeeUnavailable
}) => {
  const [search, setSearch] = useState('');

  // Clear search when drawer closes
  React.useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  const result = useMemo(() => {
    if (!shift || !onCallSchedule?.shifts) return { stats: {}, eligibleStaff: [] };
    const s = {};
    const m = currentMonth.getMonth(), y = currentMonth.getFullYear();
    onCallSchedule.shifts.forEach(x => { 
      const d = parseLocalDate(String(x.date).slice(0, 10));
      if (x.employee && d.getMonth() === m && d.getFullYear() === y) { 
        const n = x.employee.id;               // by staff id 
        if (!s[n]) s[n] = { total: 0, wknd: 0 };
        s[n].total += 1;
        if (d.getDay() === 0 || d.getDay() === 6) s[n].wknd += 1;
      } 
    });
    const eligible = (employees || []).filter(emp => {
      // Pool (on-call grade, title as fallback), rotation, leave and spacing
      // all come from the one validator, so this list and Check conflicts
      // can never disagree about who may take a role.
      const check = validateOnCallAssignment(emp, shift.date, shift.role, onCallSchedule.shifts, schedule, { date: shift.date, role: shift.role });
      if (!check.valid) return false;
      if (search && !emp.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).sort((a, b) => (s[a.id]?.total || 0) - (s[b.id]?.total || 0));
    return { stats: s, eligibleStaff: eligible };
  }, [shift, employees, onCallSchedule, search, currentMonth, schedule]);

  if (!shift) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Manage Assignment">
      <div className="flex flex-col h-full">
        <div className="p-6 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-500 uppercase tracking-widest">{roleName(rolesOf(schedule), shift.role)}</div>
            <span className="text-slate-300">•</span>
            <div className="text-sm font-bold text-slate-900">{new Date(shift.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="ghost" onClick={() => onPrepareSwap(shift)} icon={ArrowLeftRight} className="w-full justify-start text-xs font-bold text-slate-600 hover:bg-white shadow-none border-none">Swap with another date</Button>
            {shift.employee && <Button variant="ghost" onClick={() => onUnassign(shift)} icon={Trash2} className="w-full justify-start text-xs font-bold text-rose-600 hover:bg-rose-50 shadow-none border-none">Unassign current staff</Button>}
          </div>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-6 py-4 border-b border-slate-100"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search eligible staff..." className="w-full h-10 pl-9 pr-4 bg-slate-100/50 border border-slate-200 rounded-xl text-xs font-bold focus:bg-white transition-all outline-none shadow-none" /></div></div>
          <div className="flex-1 overflow-y-auto p-6 space-y-2 custom-scrollbar">
            {result.eligibleStaff.length === 0 ? <div className="py-12 text-center text-xs font-bold text-slate-300 italic">No eligible staff found</div> : 
              result.eligibleStaff.map(emp => (
                <button key={emp.id || emp.name} onClick={() => onAssign(shift, emp)} className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-teal-200 hover:bg-teal-50/30 transition-all group shadow-none">
                  <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 group-hover:bg-white group-hover:text-teal-600">{emp.name.substring(0, 2).toUpperCase()}</div><span className="text-xs font-bold text-slate-700">{emp.name}</span></div>
                  <div className="flex items-center gap-2"><span className="text-[10px] font-bold text-slate-400">{result.stats[emp.id]?.total || 0} ({result.stats[emp.id]?.wknd || 0}W)</span><ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-teal-500" /></div>
                </button>
              ))
            }
          </div>
        </div>
      </div>
    </Drawer>
  );
};

// --- SUB-COMPONENT: CLINICAL SHIFT DRAWER ---
const ClinicalShiftDrawer = ({
  isOpen, onClose, shift, employees, onAssign, onUnassign, onToggleLock, onLabelChange,
  isEmployeeUnavailable, schedule, onCallSchedule, crossCoverEnabled = false
}) => {
  const [search, setSearch] = useState('');
  // Local mirror of the free-text assignment label. Seeded from the shift and
  // committed on blur so we don't fire a schedule update on every keystroke.
  const [labelDraft, setLabelDraft] = useState('');
  React.useEffect(() => {
    setLabelDraft((shift && (shift.assignmentLabel || shift.assignment_label)) || '');
  }, [shift]);

  // Clear search when drawer closes
  React.useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  const eligibleStaff = useMemo(() => {
    if (!shift || !employees) return [];
    
    // Use grid context if available, fallback to shift.start
    const date = shift.gridDate || new Date(shift.start);
    // Which half of the day the slot is in comes from its START TIME, never from
    // its short code (Oct 2026): the grid row's code is "MID", "A", "N"..., and
    // passing it on made leave match only full days and the post-call check run
    // only for a type literally coded "PM". Two cut-offs, each the one its
    // backend twin uses: leave and post-call split the day at 13:00 (the
    // solver's am_pm_threshold_hour / pm_start_hour); the grid's half-day
    // collision check buckets at noon, like the grid itself.
    const startHour = new Date(shift.start).getHours();
    const leaveHalf = startHour < 13 ? 'AM' : 'PM';
    const timeBucket = startHour < 12 ? 'AM' : 'PM';

    const crossCoverById = {};
    const filtered = employees.filter(emp => {
      // 1. Skills: the solver's own test (services/slots.meetsSkillGate) --
      // floaters skip it, as they do in required_skill. (Until Oct 2026 a
      // TITLE could also satisfy a skill; the solver never did.)
      if (!meetsSkillGate(emp, shift)) return false;

      // --- SLOT SENIORITY GATE --------------------------------------------
      // NUMERIC. If the slot carries a rank ceiling (max_rank, 0 = most
      // senior), an employee is eligible only if their own rank -- set on their
      // staff record -- is at least that senior (emp.rank <= max_rank).
      const slotMaxRank = (shift.maxRank ?? shift.max_rank);
      const empRank = rankOf(emp);
      if (slotMaxRank !== null && slotMaxRank !== undefined && empRank > slotMaxRank) return false;
      // Seniority FLOOR (min_rank): the other end of a support rank band, e.g.
      // "an MO, not a consultant". Mirrors the solver's required_skill gate.
      const slotMinRank = (shift.minRank ?? shift.min_rank);
      if (slotMinRank !== null && slotMinRank !== undefined && empRank < slotMinRank) return false;

      // 2. Leave check
      if (isEmployeeUnavailable(schedule, emp, date, leaveHalf)) return false;
      // Rotation dates: outside their posting a person is not rosterable
      // (mirrors the solver's leave rule). No dates = always available.
      if (!isOnRotation(emp, toLocalISO(date))) return false;

      // 3. Clinical Collision Check. Someone already working this half-day is
      // hidden — UNLESS the cross_coverage rule is enabled and their other shift
      // is the SAME BLOCK (same hours, same shift type) as this slot: then a
      // planner may deliberately double them up, and the candidate is shown with
      // a cross-cover tag so the choice is visible. Mirrors the solver's rule.
      const ds = toLocalISO(date);
      const targetType = String(shift.shiftTypeId ?? shift.shift_type_id ?? '').toLowerCase();
      const sameBlock = (s) => s.start === shift.start && s.end === shift.end &&
        String(s.shiftTypeId ?? s.shift_type_id ?? '').toLowerCase() === targetType;
      let crossCoverWith = null;
      const isAlreadyAssigned = (schedule.shifts || []).some(s => {
        if (s.id === shift.id) return false;
        const sDate = s.start.split('T')[0];
        const sBlock = new Date(s.start).getHours() < 12 ? 'AM' : 'PM';
        if (!(sDate === ds && sBlock === timeBucket && samePerson(s.employee, emp))) return false;
        if (crossCoverEnabled && sameBlock(s)) { crossCoverWith = slotLocationName(s); return false; }
        return true;
      });
      if (isAlreadyAssigned) return false;
      crossCoverById[emp.id] = crossCoverWith;

      // 4. Post-Call Constraint
      if (leaveHalf === 'PM' && onCallSchedule?.shifts) {
        const prevDate = new Date(date);
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateStr = toLocalISO(prevDate);
        const wasOnCallPrevDay = onCallSchedule.shifts.some(s => 
          s.date === prevDateStr && s.employee && samePerson(s.employee, emp)
        );
        if (wasOnCallPrevDay) return false;
      }

      // 5. Search check
      if (search && !emp.name.toLowerCase().includes(search.toLowerCase())) return false;

      return true;
    });

    // Sort by seniority rank, ascending (0 = Consultant = most senior, per the
    // system-wide convention in domain.py/StaffTab.js), then alphabetical.
    // This block previously assumed the OPPOSITE scale (comment claimed
    // "3: Consultant... higher rank = more senior") and sorted descending,
    // which actually listed the most junior staff first and consultants
    // last -- backwards from both its own stated intent and every other
    // rank-aware view in the app.
    return [...filtered].sort((a, b) => {
      const rankA = a.rank ?? 4;
      const rankB = b.rank ?? 4;
      if (rankA !== rankB) return rankA - rankB; // Ascending rank: seniors first
      return a.name.localeCompare(b.name);
    }).map(e => ({ ...e, crossCoverWith: crossCoverById[e.id] || null }));
  }, [shift, employees, search, isEmployeeUnavailable, schedule, onCallSchedule, crossCoverEnabled]);

  if (!shift) return null;

  const date = shift.gridDate || new Date(shift.start);
  const timeBlock = shift.gridBlock || (date.getHours() < 12 ? 'AM' : 'PM');

  // Grouping for visual headers
  const staffGroups = [];
  if (eligibleStaff.length > 0) {
    let currentLabel = null;
    eligibleStaff.forEach(emp => {
      const r = emp.rank ?? 4;
      // Corrected to the real ascending scale (0 = most senior) alongside
      // the sort fix above -- these thresholds were inverted the same way.
      let label = "Other Staff";
      if (r === 0) label = "Consultants";
      else if (r === 1) label = "Senior Residents";
      else label = "Residents & Medical Officers";
      // Compare LABELS, not raw ranks: ranks 2/3/4 all collapse into the
      // same "Residents & Medical Officers" header, so a rank transition
      // between any of those must not re-trigger a duplicate header.
      if (label !== currentLabel) {
        staffGroups.push({ isHeader: true, label, id: `header-${label}` });
        currentLabel = label;
      }
      staffGroups.push({ ...emp, isHeader: false });
    });
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Manage Support Slot">
      <div className="flex flex-col h-full">
        <div className="p-6 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-500 uppercase tracking-widest">{slotLocationName(shift)}</div>
            <span className="text-slate-300">•</span>
            <div className="text-sm font-bold text-slate-900">{date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ({timeBlock})</div>
          </div>
          {shift.employee && (
            <Button variant="ghost" onClick={() => { onUnassign(shift.id); onClose(); }} icon={Trash2} className="w-full justify-start text-xs font-bold text-rose-600 hover:bg-rose-50 shadow-none border-none">Unassign current staff</Button>
          )}
          {shift.employee && onToggleLock && (
            <div className="mt-2 flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-slate-200">
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-600">
                {shift.locked
                  ? <><Lock className="w-3.5 h-3.5 text-amber-600" /> Locked — the solver will not move this</>
                  : <><Unlock className="w-3.5 h-3.5 text-slate-300" /> Not locked — the solver may reassign this</>}
              </div>
              <Button variant="ghost" onClick={() => onToggleLock(shift.id)} className="text-[10px] font-bold shadow-none border-none">
                {shift.locked ? 'Unlock' : 'Lock'}
              </Button>
            </div>
          )}
          {shift.employee && onLabelChange && (
            <div className="mt-3">
              <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Assignment label</label>
              <input
                type="text"
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                onBlur={() => onLabelChange(shift.id, labelDraft.trim())}
                placeholder="e.g. Table 4, Station A"
                className="w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:border-teal-300 transition-all outline-none shadow-none"
              />
            </div>
          )}
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search eligible staff..." className="w-full h-10 pl-9 pr-4 bg-slate-100/50 border border-slate-200 rounded-xl text-xs font-bold focus:bg-white transition-all outline-none shadow-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-2 custom-scrollbar">
            {staffGroups.length === 0 ? <div className="py-12 text-center text-xs font-bold text-slate-300 italic">No eligible staff found</div> :
              staffGroups.map(item => item.isHeader ? (
                <div key={item.id} className="pt-4 pb-2 px-2 first:pt-0">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">{item.label}</span>
                </div>
              ) : (
                <button key={item.name} onClick={() => { onAssign(shift.id, item); onClose(); }} className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-teal-200 hover:bg-teal-50/30 transition-all group shadow-none">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 group-hover:bg-white group-hover:text-teal-600">
                      {item.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-xs font-bold text-slate-700">{item.name}</span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{item.title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.crossCoverWith && (
                      <span className="text-[8px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title={`Already on ${item.crossCoverWith} these hours — assigning here is a cross-cover (last resort)`}>
                        Cross-cover · {item.crossCoverWith}
                      </span>
                    )}
                    <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-teal-500" />
                  </div>
                </button>
              ))
            }
          </div>
        </div>
      </div>
    </Drawer>
  );
};

// Count backend-verified conflicts in a validate-roster result: soft-rule
// violations plus one for a hard-rule (infeasible) break. Returns null for
// no/failed check. Live heuristic logs (mandatory gaps etc.) are deliberately
// EXCLUDED — they live on the Analysis badge; folding them in would leave the
// check button permanently red for any unfilled slot.
const conflictCount = (check) => {
  if (!check || check.error) return null;
  return (check.feasible === false ? 1 : 0) + (check.violations?.length || 0);
};

const DashboardTab = ({
  schedule, loading, solving, loadDemoData, showLoadDropdown, setShowLoadDropdown,
  selectedView, setSelectedView, startSolving, stopSolving,
  selectedLocation, setSelectedLocation, selectedWeek, setSelectedWeek, gridMode, setGridMode, weekDates, solveWindowDates, solveScope, setSolveScope, computeSolveWindow, solverStatus,
  getAvailableEmployeesForDay, locationCoverage, employeeStats, getDailyStaffingBuffer, getShiftsForDay,
  getEmployeeColor, isEmployeeUnavailable, publishSchedule, handleFileUpload, draftDirty, draftDates, onCallDirty,
  fetchWeekRange, onSetClosedDate,
  onCallSchedule, setOnCallSchedule, isSolvingOnCall, startSolvingOnCall, publishOnCall,
  onClinicalAssign, onClinicalUnassign, onClinicalToggleLock, onClearWeek, onClinicalLabel, onCheckConflicts,
  rosterCheck, rosterChecking, crossCoverEnabled = false, constraintLabels
}) => {
  // Public holidays: every clinic and OT is shut, so the day has no slots.
  const closedDates = useMemo(() => new Set(schedule?.closedDates || schedule?.closed_dates || []), [schedule]);
  const [managementMode, setManagementMode] = React.useState('clinical');
  // Publish auto-check flow: pending confirm-dialog payload + in-flight flag.
  const [confirmPublish, setConfirmPublish] = React.useState(null);
  // Clear confirmation: a plain boolean is enough — unlike Publish, Clear has
  // no auto-check step, it always asks before wiping the window.
  const [confirmClear, setConfirmClear] = React.useState(false);
  const [publishChecking, setPublishChecking] = React.useState(false);

  // --- shift-type aware code resolution ------------------------------------
  // NOTE: shiftBucketing.getMeta() collapses any non-AM/PM short code back to
  // an AM/PM bucket -- its hour fallback reassigns `shiftCode = fallbackBucket`,
  // so an ED 22:00 shift reports shiftCode 'PM', not 'N'. The true code is
  // therefore resolved from schedule.shiftTypes directly; the bucketer is used
  // only for (a) the AM/PM half-day bucket that leave lookups are keyed on and
  // (b) legacy shifts that carry no shiftTypeId at all.
  const bucketer = useMemo(() => createShiftBucketer(schedule), [schedule]);

  const shiftTypesById = useMemo(() => {
    const map = new Map();
    ((schedule?.shiftTypes || schedule?.shift_types) || []).forEach(t => {
      if (t && t.id) map.set(String(t.id), t);
    });
    return map;
  }, [schedule]);

  // Short code used for grid columns, e.g. 'AM' | 'PM' | 'N'.
  const codeOf = React.useCallback((s) => {
    const stid = s?.shiftTypeId || s?.shift_type_id;
    if (stid && shiftTypesById.has(String(stid))) {
      const t = shiftTypesById.get(String(stid));
      return String(t.shortCode || t.short_code || t.id).trim().toUpperCase();
    }
    return bucketer.getMeta(s).bucket || 'PM';
  }, [shiftTypesById, bucketer]);
  // Which (location, day, code) cells have any slot at all -- built once per
  // schedule instead of scanning every shift for every cell on every render.
  const openCells = useMemo(() => {
    const set = new Set();
    for (const sh of schedule?.shifts || []) set.add(`${sh.location}|${String(sh.start || '').slice(0, 10)}|${codeOf(sh)}`);
    return set;
  }, [schedule?.shifts, codeOf]);

  // AM/PM half-day bucket. Leave is stored per date as full / AM / PM, so
  // leave lookups must use this, never the short code.
  const bucketOf = React.useCallback(
    (s) => bucketer.getMeta(s).bucket || 'PM', [bucketer]);

  // Columns rendered per day. Locations whose visible shifts are only AM/PM
  // keep the exact two-column layout they have today; anything else expands.
  const displayCodes = useMemo(() => {
    const codes = new Set();
    (weekDates || []).forEach(d => {
      (getShiftsForDay(d) || []).forEach(sh => {
        if (gridMode === 'location' && selectedLocation !== 'all'
            && sh.location !== selectedLocation) return;
        const c = codeOf(sh);
        if (c) codes.add(c);
      });
    });
    const arr = [...codes];
    if (arr.length === 0 || arr.every(c => c === 'AM' || c === 'PM')) return ['AM', 'PM'];
    const rank = (c) => (c === 'AM' ? 0 : c === 'PM' ? 1 : 2);
    return arr.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [weekDates, getShiftsForDay, codeOf, gridMode, selectedLocation]);

  // On-call view: the night-by-role table, or one row per person.
  const [onCallView, setOnCallView] = React.useState('roles');
  const [showAudit, setShowAudit] = React.useState(false);
  const [currentMonth, setCurrentMonth] = React.useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [safetyModal, setSafetyModal] = React.useState({ isOpen: false, reason: '' });

  // The department's on-call roles (Settings -> On-call roles); the default four if none.
  const onCallRoles = React.useMemo(() => rolesOf(schedule), [schedule]);
  const onCallMonthDays = React.useMemo(() => {
    const year = currentMonth.getFullYear(), month = currentMonth.getMonth();
    const date = new Date(year, month, 1), res = [];
    while (date.getMonth() === month) { res.push(new Date(date)); date.setDate(date.getDate() + 1); }
    return res;
  }, [currentMonth]);

  const [editingOnCallShift, setEditingOnCallShift] = React.useState(null);
  const [editingClinicalShift, setEditingClinicalShift] = React.useState(null);
  // Which collapsed cells are expanded in-place. Keyed on location::dateISO::code
  // so expansion is per cell and survives re-renders.
  const [expandedCells, setExpandedCells] = React.useState(() => new Set());
  const toggleExpanded = React.useCallback((key) => {
    setExpandedCells(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);
  const [onCallSwapSource, setOnCallSwapSource] = React.useState(null);
  const [pendingOnCallAction, setPendingOnCallAction] = React.useState(null);

  const recalculateOnCallStats = (shifts, targetDate) => {
    const stats = Object.fromEntries(onCallRoles.map(r => [r.key, {}]));
    const m = targetDate.getMonth(), y = targetDate.getFullYear();
    shifts.forEach(s => {
      const sd = parseLocalDate(String(s.date).slice(0, 10));
      if (s.employee && sd.getMonth() === m && sd.getFullYear() === y) {
        const category = s.role;
        if (!stats[category]) stats[category] = {};
        const isWknd = sd.getDay() === 0 || sd.getDay() === 6;
        const who = s.employee.id;              // keyed by staff id
        if (!stats[category][who]) {
          stats[category][who] = { total: 0, wknd: 0 };
        }
        stats[category][who].total += 1;
        if (isWknd) stats[category][who].wknd += 1;
      }
    });
    return stats;
  };

  const handleOnCallAssign = (shift, employee, force = false) => {
    if (!onCallSchedule || !setOnCallSchedule) return;
    
    if (!force) {
      const check = validateOnCallAssignment(employee, shift.date, shift.role, onCallSchedule.shifts, schedule);
      if (!check.valid) { 
        setEditingOnCallShift(null); // Auto-close drawer
        setSafetyModal({ isOpen: true, reason: check.reason, isWarning: false }); 
        return; 
      }
      if (check.warning) {
        setEditingOnCallShift(null); // Auto-close drawer
        setPendingOnCallAction({ type: 'assign', params: [shift, employee], reason: check.reason });
        setSafetyModal({ isOpen: true, reason: check.reason, isWarning: true });
        return;
      }
    }

    const shifts = onCallSchedule.shifts || [];
    const exists = shifts.some(s => s.date === shift.date && s.role === shift.role);
    let updated;
    if (exists) updated = shifts.map(s => (s.date === shift.date && s.role === shift.role) ? { ...s, employee, isManual: true } : s);
    else updated = [...shifts, { ...shift, id: onCallId(shift.date, shift.role, onCallRoles), employee, isManual: true }];
    setOnCallSchedule({ ...onCallSchedule, shifts: updated, stats: recalculateOnCallStats(updated, currentMonth) });
    setEditingOnCallShift(null);
    setPendingOnCallAction(null);
    setSafetyModal({ isOpen: false, reason: '' });
  };

  const handleOnCallUnassign = (shift) => {
    if (!onCallSchedule || !setOnCallSchedule) return;
    const shifts = onCallSchedule.shifts || [];
    const exists = shifts.some(s => s.date === shift.date && s.role === shift.role);
    let updated;
    if (exists) updated = shifts.map(s => (s.date === shift.date && s.role === shift.role) ? { ...s, employee: null, isManual: true } : s);
    else updated = [...shifts, { ...shift, id: onCallId(shift.date, shift.role, onCallRoles), employee: null, isManual: true }];
    setOnCallSchedule({ ...onCallSchedule, shifts: updated, stats: recalculateOnCallStats(updated, currentMonth) });
    setEditingOnCallShift(null);
  };

  const handleOnCallSwapExecute = (targetShift, force = false) => {
    if (!onCallSwapSource || !onCallSchedule?.shifts || !setOnCallSchedule) return;
    const empS = onCallSwapSource.employee, empT = targetShift.employee;
    
    if (!force) {
      if (empS) {
        const checkS = validateOnCallAssignment(empS, targetShift.date, targetShift.role, onCallSchedule.shifts, schedule, { date: onCallSwapSource.date, role: onCallSwapSource.role });
        if (!checkS.valid) { setSafetyModal({ isOpen: true, reason: `Swap blocked for ${empS.name}: ${checkS.reason}`, isWarning: false }); return; }
        if (checkS.warning) {
          setPendingOnCallAction({ type: 'swap', params: [targetShift], reason: checkS.reason });
          setSafetyModal({ isOpen: true, reason: checkS.reason, isWarning: true });
          return;
        }
      }
      if (empT) {
        const checkT = validateOnCallAssignment(empT, onCallSwapSource.date, onCallSwapSource.role, onCallSchedule.shifts, schedule, { date: targetShift.date, role: targetShift.role });
        if (!checkT.valid) { setSafetyModal({ isOpen: true, reason: `Swap blocked for ${empT.name}: ${checkT.reason}`, isWarning: false }); return; }
        if (checkT.warning) {
          setPendingOnCallAction({ type: 'swap', params: [targetShift], reason: checkT.reason });
          setSafetyModal({ isOpen: true, reason: checkT.reason, isWarning: true });
          return;
        }
      }
    }

    const updated = onCallSchedule.shifts.map(s => {
      if (s.date === onCallSwapSource.date && s.role === onCallSwapSource.role) return { ...s, employee: empT, isManual: true };
      if (s.date === targetShift.date && s.role === targetShift.role) return { ...s, employee: empS, isManual: true };
      return s;
    });
    setOnCallSchedule({ ...onCallSchedule, shifts: updated, stats: recalculateOnCallStats(updated, currentMonth) });
    setOnCallSwapSource(null);
    setPendingOnCallAction(null);
    setSafetyModal({ isOpen: false, reason: '' });
  };

  const handleConfirmPendingAction = () => {
    if (!pendingOnCallAction) return;
    if (pendingOnCallAction.type === 'assign') {
      handleOnCallAssign(...pendingOnCallAction.params, true);
    } else if (pendingOnCallAction.type === 'swap') {
      handleOnCallSwapExecute(...pendingOnCallAction.params, true);
    }
  };


  const clinicalAuditLogs = useMemo(() => {
    if (!schedule?.shifts || !weekDates || weekDates.length === 0) return [];
    const weekISO = weekDates.map(d => toLocalISO(d));
    const logs = schedule.shifts.filter(s => weekISO.includes(s.start.split('T')[0])).map(s => {
      // `block` labels the row (noon, like the grid); leave is judged at the
      // solver's 13:00 cut-off, so the audit and the solver agree.
      const dObj = new Date(s.start), block = dObj.getHours() < 12 ? 'AM' : 'PM';
      const leaveHalf = dObj.getHours() < 13 ? 'AM' : 'PM';
      if (!s.employee) {
        const isL = isLeadSlot(s);
        const mandatory = s.isMandatory !== undefined ? s.isMandatory : isL;
        // Empty OPTIONAL (above-minimum) slots are overflow capacity for spare
        // manpower, empty by design when nobody is free — deliberately NOT an
        // audit item. Only mandatory gaps are real fulfilment problems.
        if (!mandatory) return null;
        return { type: "Mandatory Gap", severity: "Critical", staff: "UNASSIGNED", date: `${dObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} (${block})`, desc: `${slotLocationName(s)} missing ${isL ? 'Lead' : 'Support'}`, location: s.location };
      }
      if (isEmployeeUnavailable(schedule, s.employee, dObj, leaveHalf)) return { type: "Leave Conflict", severity: "Critical", staff: s.employee.name, date: `${dObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} (${block})`, desc: `${s.employee.name} on leave`, location: s.location };
      return null;
    }).filter(Boolean);
    // Cross-covers: one person on two wards in the same hours. Surfaced as a
    // High-severity entry (not Critical — it is a permitted last resort) so the
    // planner can see every double-up the solver or a colleague introduced.
    groupBlocks(schedule.shifts.filter(s => s.employee && weekISO.includes(s.start.split('T')[0]))).forEach(members => {
      if (members.length < 2) return;
      const s = members[0];
      const dObj = new Date(s.start), block = dObj.getHours() < 12 ? 'AM' : 'PM';
      const wards = members.map(m => slotLocationName(m)).join(' + ');
      logs.push({ type: "Cross-cover", severity: "High", staff: s.employee.name, date: `${dObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} (${block})`, desc: `${s.employee.name} covering ${wards} at the same time`, location: wards });
    });
    return logs;
  }, [schedule, weekDates, isEmployeeUnavailable]);

  const handleSolveWeekAction = React.useCallback(() => {
    if (!schedule || !weekDates || weekDates.length === 0) return;
    const solveDates = (solveWindowDates && solveWindowDates.length) ? solveWindowDates : weekDates;
    const startISO = toLocalISO(solveDates[0]);
    const endISO = toLocalISO(solveDates[solveDates.length - 1]);
    // Send the window plus the context its rules can see, not every shift ever
    // loaded (services/rosterScope.js); the server fills in published history.
    startSolving({ ...schedule, shifts: scopeShifts(schedule.shifts, startISO, endISO), solveWindowStart: startISO, solveWindowEnd: endISO });
  }, [schedule, weekDates, solveWindowDates, startSolving]);

  const doPublish = React.useCallback(() => {
    if (!schedule || !weekDates || weekDates.length === 0) return;
    // Publish must cover the same window that was solved, not just the visible
    // week -- otherwise a month-scope solve would persist only 7 of its days.
    const pubDates = (solveWindowDates && solveWindowDates.length) ? solveWindowDates : weekDates;
    const windowISO = pubDates.map(d => toLocalISO(d));
    // The window goes along so only ITS days stop counting as unpublished.
    publishSchedule({ ...schedule, shifts: schedule.shifts.filter(s => windowISO.includes(s.start.split('T')[0])) }, windowISO);
  }, [schedule, weekDates, solveWindowDates, publishSchedule]);

  // Same window rule as Solve/Publish: the solve scope if one is set,
  // otherwise the visible week. Wipes every assignment and lock in it.
  const doClear = React.useCallback(() => {
    if (!schedule || !weekDates || weekDates.length === 0 || !onClearWeek) return;
    const clearDates = (solveWindowDates && solveWindowDates.length) ? solveWindowDates : weekDates;
    onClearWeek(clearDates.map(d => toLocalISO(d)));
  }, [schedule, weekDates, solveWindowDates, onClearWeek]);

  // Publish auto-check (Sep 2026): validate before publishing. A CURRENT check
  // result is reused (App hands us null once a result goes stale, so a non-null
  // rosterCheck is by definition fresh); otherwise run one now. Conflicts or a
  // failed check route through a confirm dialog — never a silent publish.
  const handlePublishClick = React.useCallback(async () => {
    if (!schedule || solving) return;
    let result = rosterCheck;
    if (!result && onCheckConflicts) {
      setPublishChecking(true);
      try { result = await onCheckConflicts(); }
      finally { setPublishChecking(false); }
    }
    if (!result || result.error) { setConfirmPublish({ unverified: true }); return; }
    const n = conflictCount(result);
    if (n > 0) setConfirmPublish({ count: n, infeasible: result.feasible === false });
    else doPublish();
  }, [schedule, solving, rosterCheck, onCheckConflicts, doPublish]);

  // The ONE window Solve, Check conflicts and Publish all act on. Named on all
  // three controls so a monthly solve is visibly followed by a monthly check
  // and a monthly publish — you never publish week by week, and the check's
  // verdict covers the whole window, not just the visible week.
  const solveWinLabel = (solveWindowDates && solveWindowDates.length)
    ? formatCompactDateRange(solveWindowDates[0], solveWindowDates[solveWindowDates.length - 1]) : '';
  // On-call's window is always the calendar month on screen.
  const onCallWinLabel = onCallMonthDays.length
    ? formatCompactDateRange(onCallMonthDays[0], onCallMonthDays[onCallMonthDays.length - 1]) : '';

  // Clear the month on screen from the on-call draft (a hand change like any
  // other: unpublished until Publish).
  const doClearOnCall = () => {
    if (!onCallSchedule?.shifts || !setOnCallSchedule) return;
    const inMonth = (ds) => {
      const d = parseLocalDate(String(ds).slice(0, 10));
      return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear();
    };
    const updated = onCallSchedule.shifts.map(s => (inMonth(s.date) ? { ...s, employee: null, isManual: true } : s));
    setOnCallSchedule({ ...onCallSchedule, shifts: updated, stats: recalculateOnCallStats(updated, currentMonth) });
  };


  const getAssignmentsForDay = (dateObj) => {
    if (!onCallSchedule?.shifts) return null;
    const ds = toLocalISO(dateObj);
    return Object.fromEntries(onCallRoles.map(r => [r.key, onCallSchedule.shifts.find(s => s.date === ds && s.role === r.key)]));
  };

  const onCallAuditLogs = useMemo(() => {
    // Local dates and month AND year (it used to compare the month only, and
    // parse 'YYYY-MM-DD' as UTC).
    const inMonth = (ds) => {
      if (!ds) return false;
      const d = parseLocalDate(String(ds).slice(0, 10));
      return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear();
    };
    const duties = (onCallSchedule?.shifts || []).filter(s => inMonth(s.date));
    // Whether that person still holds a duty that night -- by staff id. An
    // audit row with no id (written before ids) cannot be checked, so it is
    // treated as stale; the overrides below are recomputed from the data anyway.
    const holds = (staffId, ds) => !!staffId && duties.some(s => idOf(s.employee) === staffId && String(s.date).slice(0, 10) === String(ds).slice(0, 10));
    // The solver's findings, minus any that no longer apply: a blackout or
    // leave row whose person has since been moved off that night is stale.
    // Unfilled roles are recounted below from the live draft, so a hand-filled
    // night stops being reported; the solver's own rows for them are dropped.
    const base = (onCallSchedule?.conflictAudit || onCallSchedule?.conflict_audit || [])
      .filter(l => inMonth(l.date))
      .filter(l => holds(l.staff_id ?? l.staffId, l.date));     // an empty role's row has no staff id
    const seen = new Set(base.map(l => `${l.type}|${l.staff_id ?? l.staffId}|${String(l.date).slice(0, 10)}`));
    const extra = duties.filter(s => s.employee).flatMap(s => {
      const ds = String(s.date).slice(0, 10);
      const name = s.employee.name;
      const out = [];
      const day = parseLocalDate(ds);
      if (isEmployeeUnavailable(schedule, s.employee, day, 'AM') || isEmployeeUnavailable(schedule, s.employee, day, 'PM'))
        out.push({ type: "Leave Conflict (On-Call)", severity: "Critical", staff: name, date: ds, desc: `${name} on leave` });
      // Someone rostered on a date they asked not to be (often a hand change).
      const emp = (schedule?.employees || []).find(e => samePerson(e, s.employee));
      const blackouts = emp?.onCallForbiddenDates || emp?.on_call_forbidden_dates || [];
      if (blackouts.includes(ds) && !seen.has(`Blackout Violation|${idOf(s.employee)}|${ds}`))
        out.push({ type: "Dispreference Override", severity: "Warning", staff: name, date: ds, desc: `${name} is on call on a date they blacked out` });
      return out;
    });
    // Every duty against the same rules a manual assignment is held to (grade,
    // rotation, leave, back-to-back nights, two roles in one night). A
    // back-to-back pair is reported once, not from both nights.
    const rules = [];
    const seenRules = new Set();
    duties.filter(s => s.employee).forEach(s => {
      const emp = (schedule?.employees || []).find(e => samePerson(e, s.employee)) || s.employee;
      const check = validateOnCallAssignment(emp, String(s.date).slice(0, 10), s.role, duties, schedule,
                                             { id: s.id, date: s.date, role: s.role });
      if (check.valid && !check.warning) return;
      if (check.code === 'leave') return;                         // reported above
      const who = idOf(s.employee);
      const key = `${check.code}|${who}|${String(s.date).slice(0, 10)}`;
      const pairKey = `b2b|${who}`;
      if (check.code === 'back_to_back') {
        const d = parseLocalDate(String(s.date).slice(0, 10));
        const next = toLocalISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
        if (seenRules.has(`${pairKey}|${next}`) || seenRules.has(`${pairKey}|${String(s.date).slice(0, 10)}`)) return;
        seenRules.add(`${pairKey}|${String(s.date).slice(0, 10)}`);
      } else if (seenRules.has(key)) return;
      seenRules.add(key);
      rules.push({ type: check.valid ? 'Rule warning (On-Call)' : 'Rule broken (On-Call)',
                   severity: check.valid ? 'Warning' : 'Critical',
                   staff: s.employee.name, date: String(s.date).slice(0, 10), desc: check.reason });
    });
    // Empty roles, counted from the draft itself -- only once a month has been
    // solved or edited, so an untouched month is not one long list of gaps.
    const unfilled = [];
    if (duties.length > 0) {
      onCallMonthDays.forEach(dateObj => {
        const ds = toLocalISO(dateObj);
        onCallRoles.forEach(({ key: role, name }) => {
          if (!duties.some(s => String(s.date).slice(0, 10) === ds && s.role === role && s.employee))
            unfilled.push({ type: 'Unfilled role', severity: 'Warning', staff: 'UNASSIGNED', date: ds, desc: `${name} has nobody on call` });
        });
      });
    }
    return [...base, ...extra, ...rules, ...unfilled];
  }, [onCallSchedule, currentMonth, schedule, isEmployeeUnavailable, onCallMonthDays, onCallRoles]);
  const onCallCritical = onCallAuditLogs.filter(l => l.severity === 'Critical').length;
  const onCallIssues = onCallAuditLogs.length;

  // On-call by person: one row each, the month across, totals first. Grouped by
  // on-call pool (the same pools the solver uses); anyone outside a pool who
  // nevertheless holds a duty this month is listed last so nothing is hidden.
  // Colour by grade; a junior role at a second site is told apart in orange.
  const roleStyle = (key) => {
    const r = onCallRoles.find(x => x.key === key);
    if (!r) return 'bg-slate-100 text-slate-600';
    if (r.grade === 0) return 'bg-[#042f2e] text-white';
    const firstSite = onCallRoles.find(x => x.grade === r.grade && x.site)?.site;
    return r.site && firstSite && r.site !== firstSite ? 'bg-orange-100 text-orange-700' : 'bg-teal-100 text-teal-800';
  };
  const renderOnCallStaffView = () => {
    const monthIso = onCallMonthDays.map(d => toLocalISO(d));
    const duties = (onCallSchedule?.shifts || []).filter(s => s.employee && monthIso.includes(String(s.date).slice(0, 10)));
    const byPerson = {};
    duties.forEach(s => { (byPerson[s.employee.id] = byPerson[s.employee.id] || {})[String(s.date).slice(0, 10)] = s; });
    const POOLS = [[0, 'Consultants'], [1, 'Senior Residents'], [2, 'Juniors']];
    const gradeOf = (e) => e.oncallRank ?? e.oncall_rank;
    const eligible = (e) => (e.isOnCallEligible ?? e.is_on_call_eligible) !== false;
    const groups = POOLS.map(([g, label]) => [label, (schedule?.employees || [])
      .filter(e => gradeOf(e) === g && (eligible(e) || byPerson[e.id]))
      .sort((a, b) => a.name.localeCompare(b.name))]);
    const pooled = new Set(groups.flatMap(([, list]) => list.map(e => e.id)));
    const others = (schedule?.employees || []).filter(e => byPerson[e.id] && !pooled.has(e.id));
    if (others.length) groups.push(['Not in an on-call pool', others]);
    const cellW = 'w-[34px] min-w-[34px]';
    return (
      <div className="bg-white rounded-[20px] border border-slate-200 overflow-x-auto custom-scrollbar">
        {/* Scrolls sideways only (31 day columns); up and down is the page's
            scroll, like every other screen. */}
        <table className="border-collapse text-[10px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="sticky left-0 bg-slate-50 p-3 text-left font-bold text-slate-400 uppercase tracking-[0.2em] min-w-[160px] border-r border-slate-200">Staff</th>
              <th className="p-2 font-bold text-slate-400 uppercase border-r border-slate-100" title="Duties this month">Total</th>
              <th className="p-2 font-bold text-slate-400 uppercase border-r border-slate-200" title="Saturday and Sunday duties">Wknd</th>
              {onCallMonthDays.map((d, i) => {
                const w = d.getDay() === 0 || d.getDay() === 6;
                return (<th key={i} className={`${cellW} p-1 text-center font-bold border-r border-slate-100 ${w ? 'bg-slate-200 text-slate-600' : 'text-slate-400'}`}>
                  <div>{d.toLocaleDateString('en-GB', { weekday: 'narrow' })}</div><div>{d.getDate()}</div></th>);
              })}
            </tr>
          </thead>
          <tbody>
            {groups.filter(([, list]) => list.length).map(([label, list]) => (
              <React.Fragment key={label}>
                <tr className="bg-slate-100/70"><td colSpan={monthIso.length + 3} className="sticky left-0 px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</td></tr>
                {list.map(e => {
                  const mine = byPerson[e.id] || {};
                  const total = Object.keys(mine).length;
                  const wknd = Object.keys(mine).filter(ds => { const d = parseLocalDate(ds).getDay(); return d === 0 || d === 6; }).length;
                  return (
                    <tr key={e.name} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="sticky left-0 bg-white p-2 pl-3 font-bold text-slate-700 truncate max-w-[160px] border-r border-slate-200">{e.name}</td>
                      <td className="p-2 text-center font-black text-[var(--primary)] border-r border-slate-100">{total}</td>
                      <td className="p-2 text-center font-bold text-slate-500 border-r border-slate-200">{wknd}</td>
                      {onCallMonthDays.map((d, i) => {
                        const ds = monthIso[i];
                        const duty = mine[ds];
                        const away = isEmployeeUnavailable(schedule, e, d, 'AM') || isEmployeeUnavailable(schedule, e, d, 'PM');
                        const offRotation = !isOnRotation(e, ds);
                        const w = d.getDay() === 0 || d.getDay() === 6;
                        return (
                          <td key={i} className={`${cellW} p-0.5 text-center border-r border-slate-50 ${w ? 'bg-slate-50' : ''}`}
                            title={duty ? `${roleName(onCallRoles, duty.role)} — ${ds}` : away ? `On leave — ${ds}` : offRotation ? `Outside rotation — ${ds}` : ds}>
                            {duty ? (
                              <button type="button" onClick={() => setEditingOnCallShift(duty)}
                                className={`w-full h-6 rounded font-black text-[9px] ${roleStyle(duty.role)}`}>
                                {roleTag(roleName(onCallRoles, duty.role))}
                              </button>
                            ) : away ? (
                              <div className="w-full h-6 rounded bg-rose-50 text-rose-300 font-bold text-[9px] flex items-center justify-center">L</div>
                            ) : offRotation ? (
                              <div className="w-full h-6 rounded bg-slate-100" />
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Which shift codes to stack for one location's row. Restricted to the codes
  // that location actually uses this week, so a weekday clinic stacks AM/PM
  // only and never renders empty N rows. Computed per row (not per cell) so a
  // location's Monday and Tuesday stacks stay vertically aligned.
  const codesForLocation = React.useCallback((location) => {
    const present = new Set();
    (weekDates || []).forEach(d => (getShiftsForDay(d) || []).forEach(sh => {
      if (sh.location === location) present.add(codeOf(sh));
    }));
    const used = displayCodes.filter(c => present.has(c));
    return used.length ? used : displayCodes.slice(0, 1);
  }, [weekDates, getShiftsForDay, codeOf, displayCodes]);

  const codesForEmployee = React.useCallback((person) => {
    const present = new Set();
    (weekDates || []).forEach(d => (getShiftsForDay(d) || []).forEach(sh => {
      if (samePerson(sh.employee, person)) present.add(codeOf(sh));
    }));
    const used = displayCodes.filter(c => present.has(c));
    return used.length ? used : [displayCodes[0] || 'AM'];
  }, [weekDates, getShiftsForDay, codeOf, displayCodes]);

  // Gutter label shown beside each stacked entry.
  const StackLabel = ({ code }) => (
    <span className={`w-5 flex-shrink-0 text-[8px] font-bold uppercase text-right leading-none pt-[13px] ${code === 'AM' || code === 'PM' ? 'text-slate-300' : 'text-teal-400'}`}>{code}</span>
  );

  // Cells holding more than this many slots collapse to a summary. Two slots
  // (lead + one support) still fit the split layout legibly, so every existing
  // clinic renders exactly as before; ED nights with min/target 2/3 collapse.
  const COLLAPSE_ABOVE = 2;

  const isMandatoryShift = (s) =>
    (s?.isMandatory ?? s?.is_mandatory ?? true) !== false;

  const renderShiftCell = (shifts, isClosed, date, timeBlock) => {
    const CELL_H = 'h-[36px]';
    // timeBlock is the row's short code ('MID', 'A', 'N'...) -- a label only.
    // Leave is judged by each slot's START TIME, at the solver's 13:00 cut-off.
    const leaveHalfOf = (slot) => (new Date(slot.start).getHours() < 13 ? 'AM' : 'PM');
    if (isClosed) return (<div className={`w-full ${CELL_H} rounded border border-dashed border-slate-200`} />);
    if (!shifts || shifts.length === 0) return (<div className={`w-full ${CELL_H} rounded border bg-slate-50 border-slate-200`} />);
    // The primary lead is the FIRST lead slot (its own fields, services/slots.js
    // -- it used to be read out of the id); any further leads and all support
    // slots render in the stacked "support" area so nothing is dropped when a
    // cell has more than one lead. Dense cells (>2 slots) use the
    // collapsed/expanded path which already renders every slot regardless.
    const isPrimaryLead = (slot) => isLeadSlot(slot) && (slot.slotPosition ?? slot.slot_position ?? 1) === 1;
    const lead = shifts.find(isPrimaryLead);
    const support = shifts.filter(slot => !isPrimaryLead(slot));

    // --- dense cells: collapsed summary that expands in-place -------------
    if (shifts.length > COLLAPSE_ABOVE) {
      const assigned = shifts.filter(s => s.employee);
      const mandatory = shifts.filter(isMandatoryShift).length;
      const conflicted = assigned.some(
        s => isEmployeeUnavailable(schedule, s.employee, date, leaveHalfOf(s)));

      // Drops land on the first unfilled slot, lead first.
      const dropTarget = (lead && !lead.employee)
        ? lead
        : (shifts.find(s => !s.employee) || lead || shifts[0]);

      // Colour encodes the min/target split: understaffed vs minimum met vs full.
      const tone = assigned.length >= shifts.length
        ? 'bg-white border-slate-200 text-slate-700'
        : assigned.length >= mandatory
          ? 'bg-amber-50/70 border-amber-200 text-amber-800'
          : 'bg-rose-50 border-rose-200 text-rose-700';

      const firstName = assigned.length ? assigned[0].employee.name : null;
      const extra = Math.max(assigned.length - 1, 0);

      const cellKey = `${shifts[0].location}::${toLocalISO(date)}::${timeBlock}`;
      const isExpanded = expandedCells.has(cellKey);

      if (!isExpanded) {
        // Collapsed summary. Click toggles the inline list; drag still drops
        // onto the first open slot.
        return (
          <DroppableSlot id={dropTarget?.id || 'empty_slot'} className={`w-full ${CELL_H} rounded`}>
            <div
              onClick={() => toggleExpanded(cellKey)}
              title="Click to show assigned staff"
              className={`relative h-full w-full rounded border flex items-center gap-1 px-1.5 overflow-hidden shadow-none cursor-pointer ${tone}`}
            >
              {conflicted && <div className="absolute inset-0 bg-rose-500/15 animate-pulse rounded z-10 pointer-events-none" />}
              <span className={`text-[9px] font-bold tabular-nums flex-shrink-0 ${assigned.length >= mandatory ? '' : 'text-rose-600'}`}>
                {assigned.length}/{shifts.length}
              </span>
              {firstName ? (
                <span className="text-[8px] font-medium truncate min-w-0">{firstName}</span>
              ) : (
                <span className="text-[7px] font-bold uppercase opacity-60 truncate">Empty</span>
              )}
              {extra > 0 && (
                <span className="text-[8px] font-bold opacity-60 flex-shrink-0 ml-auto">+{extra}</span>
              )}
              <ChevronDown className="w-2.5 h-2.5 flex-shrink-0 opacity-40" />
            </div>
          </DroppableSlot>
        );
      }

      // Expanded: full roster for this cell. Every slot is a drop target and,
      // if filled, shows an unassign affordance. Lead first, then supports;
      // mandatory before optional within each; unfilled slots render as "Open".
      const ordered = [...shifts].sort((x, y) => {
        const lx = isLeadSlot(x) ? 0 : 1, ly = isLeadSlot(y) ? 0 : 1;
        if (lx !== ly) return lx - ly;
        const mx = isMandatoryShift(x) ? 0 : 1, my = isMandatoryShift(y) ? 0 : 1;
        return mx - my;
      });

      return (
        <div className={`w-full rounded border shadow-sm ${tone}`}>
          <div
            onClick={() => toggleExpanded(cellKey)}
            title="Click to collapse"
            className="flex items-center gap-1 px-1.5 h-[20px] border-b border-black/5 cursor-pointer"
          >
            <span className={`text-[9px] font-bold tabular-nums ${assigned.length >= mandatory ? '' : 'text-rose-600'}`}>
              {assigned.length}/{shifts.length}
            </span>
            <span className="text-[7px] font-bold uppercase opacity-50">{timeBlock}</span>
            <ChevronUp className="w-2.5 h-2.5 ml-auto opacity-40" />
          </div>
          <div className="p-1 space-y-1">
            {ordered.map(slot => {
              const isLead = isLeadSlot(slot);
              const opt = !isMandatoryShift(slot);
              const roleTag = isLead ? 'LEAD' : (opt ? 'OPT' : 'SUP');
              if (slot.employee) {
                const unavail = isEmployeeUnavailable(schedule, slot.employee, date, leaveHalfOf(slot));
                return (
                  // Clicking a filled slot opens the editing drawer (same as a
                  // non-collapsed filled cell). This is what makes per-assignment
                  // actions — including the free-text label — reachable for
                  // collapsed multi-headcount shifts, not just expanded/open ones.
                  // The unassign (X) button stops propagation so it still works.
                  <div
                    key={slot.id}
                    onClick={() => setEditingClinicalShift({ ...slot, gridDate: date, gridBlock: timeBlock })}
                    title="Click to edit this assignment"
                    className={`flex items-center gap-1 rounded px-1 py-0.5 bg-white/70 border cursor-pointer hover:border-teal-300 ${unavail ? 'border-rose-300' : 'border-black/5'}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getEmployeeColor(slot.employee.name) }} />
                    <span className="text-[9px] font-bold text-slate-700 truncate min-w-0">{slot.employee.name}</span>
                    {slot.locked && <span title="Locked — the solver will not move this" className="flex-shrink-0"><Lock className="w-2 h-2 text-amber-600" /></span>}
                    <span className="text-[6px] font-bold uppercase text-slate-300 flex-shrink-0">{roleTag}</span>
                    {slot.assignmentLabel && <span className="text-[6px] font-bold uppercase text-teal-500 flex-shrink-0 truncate max-w-[40px]">{slot.assignmentLabel}</span>}
                    {unavail && <span className="text-[6px] font-bold uppercase text-rose-500 flex-shrink-0">leave</span>}
                    <button
                      type="button"
                      title={`Unassign ${slot.employee.name}`}
                      onClick={(e) => { e.stopPropagation(); onClinicalUnassign && onClinicalUnassign(slot.id); }}
                      className="ml-auto flex-shrink-0 text-slate-300 hover:text-rose-500 transition-colors"
                    >
                      <X className="w-2.5 h-2.5 stroke-[3px]" />
                    </button>
                  </div>
                );
              }
              // Open slot — a drop target that also opens the drawer for a picker.
              return (
                <DroppableSlot key={slot.id} id={slot.id} className="w-full rounded">
                  <div
                    onClick={() => setEditingClinicalShift({ ...slot, gridDate: date, gridBlock: timeBlock })}
                    title="Open slot — drop a name here or click to assign"
                    className="flex items-center gap-1 rounded px-1 py-0.5 border border-dashed border-slate-300 bg-white/40 cursor-pointer hover:border-slate-400"
                  >
                    <Plus className="w-2.5 h-2.5 text-slate-400 flex-shrink-0" />
                    <span className="text-[8px] font-bold uppercase text-slate-400">Open</span>
                    <span className="text-[6px] font-bold uppercase text-slate-300 ml-auto flex-shrink-0">{roleTag}</span>
                  </div>
                </DroppableSlot>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={`flex flex-col w-full ${CELL_H}`}>
        <DroppableSlot
          id={lead?.id || 'empty_lead'}
          className={`w-full rounded ${support.length > 0 ? 'h-1/2' : 'h-full'}`}
          onClick={lead ? (() => setEditingClinicalShift({ ...lead, gridDate: date, gridBlock: timeBlock })) : undefined}
        >
          {lead?.employee ? (
            <div className="relative h-full w-full group/doc shadow-none" title={lead.assignmentLabel ? `Label: ${lead.assignmentLabel}` : undefined}>
              {isEmployeeUnavailable(schedule, lead.employee, date, leaveHalfOf(lead)) && <div className="absolute inset-0 bg-rose-500/20 animate-pulse rounded z-10 pointer-events-none" />}
              <DraggableEmployee id={lead.id} name={lead.employee.name} className={`bg-white text-slate-700 h-full flex items-center justify-center text-[9px] font-bold w-full rounded border shadow-none ${isEmployeeUnavailable(schedule, lead.employee, date, leaveHalfOf(lead)) ? 'border-rose-500' : 'border-slate-200'}`} />
              {lead.locked && <span title="Locked — the solver will not move this" className="absolute top-0 left-0 z-10 bg-amber-500 rounded-tl rounded-br p-0.5 pointer-events-none"><Lock className="w-2 h-2 text-white" /></span>}
              {lead.assignmentLabel && <span className="absolute bottom-0 left-0 z-10 bg-teal-500 text-white text-[6px] font-bold px-1 rounded-tr rounded-bl leading-tight max-w-full truncate pointer-events-none">{lead.assignmentLabel}</span>}
              <button type="button" title={`Unassign ${lead.employee.name}`} onClick={(e) => { e.stopPropagation(); onClinicalUnassign && onClinicalUnassign(lead.id); }} className="absolute top-0 right-0 z-20 opacity-0 group-hover/doc:opacity-100 transition-opacity bg-white/90 rounded-bl border border-slate-200 text-slate-400 hover:text-rose-500 p-0.5"><X className="w-2.5 h-2.5 stroke-[3px]" /></button>
            </div>
          ) : <div className="bg-rose-50 border border-rose-200 h-full w-full rounded flex items-center justify-center shadow-none cursor-pointer hover:bg-rose-100 transition-colors"><span className="text-[7px] font-bold text-rose-300 uppercase">+ Assign</span></div>}
        </DroppableSlot>
        {support.length > 0 && (
          <div className="flex flex-row h-1/2">
            {support.map(s => (
              <DroppableSlot key={s.id} id={s.id} className="flex-1 min-w-0 h-full rounded shadow-none" onClick={() => setEditingClinicalShift({ ...s, gridDate: date, gridBlock: timeBlock })}>
                {s.employee ? (
                  <div className="relative h-full w-full group/doc shadow-none">
                    {isEmployeeUnavailable(schedule, s.employee, date, leaveHalfOf(s)) && <div className="absolute inset-0 bg-rose-500/10 animate-pulse rounded z-10 pointer-events-none" />}
                    <DraggableEmployee id={s.id} name={s.employee.name} className={`bg-slate-50 text-slate-600 h-full flex items-center justify-center text-[8px] font-bold w-full rounded border shadow-none ${isEmployeeUnavailable(schedule, s.employee, date, leaveHalfOf(s)) ? 'border-rose-400' : 'border-slate-200'}`} />
                    {s.locked && <span title="Locked — the solver will not move this" className="absolute top-0 left-0 z-10 bg-amber-500 rounded-tl rounded-br p-0.5 pointer-events-none"><Lock className="w-2 h-2 text-white" /></span>}
                    <button type="button" title={`Unassign ${s.employee.name}`} onClick={(e) => { e.stopPropagation(); onClinicalUnassign && onClinicalUnassign(s.id); }} className="absolute top-0 right-0 z-20 opacity-0 group-hover/doc:opacity-100 transition-opacity bg-white/90 rounded-bl border border-slate-200 text-slate-400 hover:text-rose-500 p-0.5"><X className="w-2 h-2 stroke-[3px]" /></button>
                  </div>
                ) : <div className="bg-slate-100/50 border border-dashed border-slate-200 h-full w-full rounded shadow-none" />}
              </DroppableSlot>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDocCell = (shifts, leave) => {
    const CELL_H = 'h-[36px]';
    if (leave) return (<div className={`w-full ${CELL_H} rounded border border-slate-200 flex items-center justify-center opacity-80 shadow-none`} style={{ backgroundImage: 'repeating-linear-gradient(45deg, #f8fafc, #f8fafc 4px, #f1f5f9 4px, #f1f5f9 8px)' }}><span className="text-[8px] font-bold text-slate-400 uppercase bg-white/60 px-1 rounded shadow-sm">Leave</span></div>);
    if (shifts.length === 0) return <div className={`w-full ${CELL_H} bg-slate-50/50 rounded border border-dashed border-slate-200 shadow-none`} />;
    return shifts.map(s => {
      const site = siteOf(s.location, schedule), c = site === 'NUH' ? 'border-l-blue-500' : site === 'NTFGH' ? 'border-l-orange-500' : site === 'AH' ? 'border-l-purple-500' : 'border-l-rose-600';
      return (<div key={s.id} className={`w-full ${CELL_H} bg-white border border-slate-200 rounded-md shadow-none flex items-center gap-1 px-2 py-1 border-l-4 ${c}`}><div className="flex flex-col min-w-0 flex-1"><span className="text-[9px] font-bold text-slate-700 truncate mb-0.5">{slotLocationName(s)}</span><span className="text-[7px] text-slate-400 font-medium uppercase">{codeOf(s)}</span></div>{s.locked && <span title="Locked — the solver will not move this" className="flex-shrink-0"><Lock className="w-2.5 h-2.5 text-amber-600" /></span>}</div>);
    });
  };

  return (
    <div className="animate-in fade-in space-y-6 shadow-none">
      <OnCallShiftDrawer isOpen={!!editingOnCallShift} onClose={() => setEditingOnCallShift(null)} shift={editingOnCallShift} employees={schedule?.employees} onCallSchedule={onCallSchedule} onAssign={handleOnCallAssign} onUnassign={handleOnCallUnassign} onPrepareSwap={(s) => { setOnCallSwapSource(s); setEditingOnCallShift(null); }} isEmployeeUnavailable={isEmployeeUnavailable} currentMonth={currentMonth} schedule={schedule} />
      <ClinicalShiftDrawer isOpen={!!editingClinicalShift} onClose={() => setEditingClinicalShift(null)} shift={editingClinicalShift} employees={schedule?.employees} onAssign={onClinicalAssign} onUnassign={onClinicalUnassign} onToggleLock={(id) => {
          onClinicalToggleLock && onClinicalToggleLock(id);
          // onClinicalToggleLock only updates `schedule` (which the grid reads
          // live) -- editingClinicalShift is a one-time snapshot taken when
          // the drawer opened, so without this the Lock/Unlock label and icon
          // would keep showing the pre-toggle state until the drawer reopens.
          setEditingClinicalShift((prev) => (prev && prev.id === id ? { ...prev, locked: !prev.locked } : prev));
        }} onLabelChange={onClinicalLabel} isEmployeeUnavailable={isEmployeeUnavailable} schedule={schedule} onCallSchedule={onCallSchedule} crossCoverEnabled={crossCoverEnabled} />
      <AuditModal isOpen={showAudit} onClose={() => setShowAudit(false)} auditLogs={managementMode === 'clinical' ? clinicalAuditLogs : onCallAuditLogs} mode={managementMode} rosterCheck={rosterCheck} rosterChecking={rosterChecking} constraintLabels={constraintLabels} />
      <SafetyBlockModal 
        isOpen={safetyModal.isOpen} 
        onClose={() => setSafetyModal({ isOpen: false, reason: '' })} 
        onConfirm={handleConfirmPendingAction}
        reason={safetyModal.reason} 
        isWarning={safetyModal.isWarning}
      />
      <ConfirmDialog
        isOpen={!!confirmPublish}
        onClose={() => setConfirmPublish(null)}
        tone={confirmPublish?.infeasible ? 'danger' : 'warning'}
        title={confirmPublish?.unverified ? 'Could not verify conflicts'
          : `${confirmPublish?.count} conflict${confirmPublish?.count === 1 ? '' : 's'} found`}
        body={confirmPublish?.unverified
          ? 'The conflict check failed to run. You can still publish, but the roster has not been validated.'
          : confirmPublish?.infeasible
            ? 'The roster breaks a hard rule. Review the audit before publishing.'
            : 'Constraint violations were found in the publish window. You can review them or publish anyway.'}
        confirmLabel="Publish anyway"
        onConfirm={() => { setConfirmPublish(null); doPublish(); }}
        secondaryLabel={confirmPublish?.unverified ? undefined : 'Review'}
        onSecondary={() => { setConfirmPublish(null); setShowAudit(true); }}
      />
      <ConfirmDialog
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        tone="danger"
        title={`Clear ${managementMode === 'clinical' ? solveWinLabel : `on-call ${onCallWinLabel}`}?`}
        body={managementMode === 'clinical'
          ? "This removes every assignment and every lock in this window from the draft. Nothing published is affected, but any unsaved manual work here will be lost."
          : "This removes every on-call duty in this month from the draft. Nothing published is affected until you press Publish, but any unsaved manual work here will be lost."}
        confirmLabel="Clear"
        onConfirm={() => { setConfirmClear(false); managementMode === 'clinical' ? doClear() : doClearOnCall(); }}
      />

      <PageHeader icon={LayoutDashboard} title="Management Console">
            {/* Draft protection lives here, next to the title, instead of in
                the action row: it comes and goes with every edit, and sitting
                among the buttons made them visibly shift position each time. */}
            {/* A stopped solve returns a complete, legal roster -- just a rougher
                one than a finished solve, and it looks identical on screen. The
                toast that explains it disappears; this does not, so a
                half-optimised roster cannot be published as the finished
                article. Clears itself on the next solve, which overwrites
                solverStatus. */}
            {managementMode === 'clinical' && solverStatus === 'CANCELLED' && (
              <div className="flex items-center gap-1.5 px-2.5 h-[26px] rounded-full bg-amber-50 border border-amber-200 mr-1" title="This roster is the best found before you pressed Stop — solve again and let it finish for a better one.">
                <Square className="w-3 h-3 text-amber-600" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 whitespace-nowrap">Stopped early</span>
              </div>
            )}
            {/* One badge however many periods are pending; which ones lives in
                its dropdown so several unpublished weeks don't crowd the header. */}
            {managementMode === 'oncall' && onCallDirty && (
              <span className="flex items-center gap-1.5 h-[22px] px-2.5 rounded-full bg-amber-50 border border-amber-300"
                    title="On-call changes exist only in this browser tab. Press Publish to save them.">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 whitespace-nowrap">Unpublished on-call</span>
              </span>
            )}
            {managementMode === 'clinical' && draftDirty && (
              <Dropdown
                align="left"
                menuClassName="min-w-[240px]"
                items={toRanges(draftDates || []).map((r) => ({
                  id: r.start,
                  label: formatDraftRange(r),
                  sublabel: `${r.days} day${r.days === 1 ? '' : 's'} not yet published`,
                }))}
                renderTrigger={({ open, toggle }) => (
                  <button type="button" onClick={toggle} aria-expanded={open} aria-haspopup="menu"
                          className="flex items-center gap-1.5 h-[22px] px-2.5 rounded-full bg-amber-50 border border-amber-300 hover:bg-amber-100"
                          style={{ borderRadius: 9999, boxShadow: 'none' }}
                          title="These changes exist only in this browser tab. Click to see which dates; publish each to make it permanent.">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 whitespace-nowrap">Unpublished draft</span>
                    <ChevronDown className={`w-3 h-3 text-amber-600 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                )}
              />
            )}
      </PageHeader>

      <Card padding="p-4" className="shadow-none">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3 shadow-none">
          <ToggleGroup label="Mode" value={managementMode} onChange={setManagementMode}
            options={[{ id: 'clinical', label: 'Clinical' }, { id: 'oncall', label: 'On-Call' }]} />
          {managementMode === 'clinical'
            ? <ToggleGroup label="View" value={gridMode} onChange={setGridMode}
                options={[{ id: 'location', label: 'Clinic' }, { id: 'doctor', label: 'Staff' }]} />
            : <ToggleGroup label="View" value={onCallView} onChange={setOnCallView}
                options={[{ id: 'roles', label: 'Roles' }, { id: 'staff', label: 'Staff' }]} />}
          <ControlGroup label="Period" className="shrink-0">{managementMode === 'clinical' ? (
            <PeriodNav
              startDate={weekDates?.[0]} endDate={weekDates?.[weekDates.length - 1]}
              onPrev={() => setSelectedWeek(selectedWeek - 1)}
              onNext={() => setSelectedWeek(selectedWeek + 1)}
            />
          ) : (
            <PeriodNav
              startDate={onCallMonthDays[0]} endDate={onCallMonthDays[onCallMonthDays.length - 1]}
              onPrev={() => { const n = new Date(currentMonth); n.setMonth(n.getMonth() - 1); setCurrentMonth(n); }}
              onNext={() => { const n = new Date(currentMonth); n.setMonth(n.getMonth() + 1); setCurrentMonth(n); }}
            />
          )}</ControlGroup>
          {/* Every roster action in one captioned group (Oct 2026): Solve, Check
              conflicts and Publish used to sit on the header row, the only tab
              that did; Clear was here already. */}
          {/* The window every action acts on, said ONCE here rather than in
              each button's label (Oct 2026). Solve, Check and Publish still act
              on exactly this window; the Solve menu shows each scope's dates. */}
          <EndGroup label={`Actions · ${managementMode === 'clinical' ? (solveWinLabel || '') : onCallWinLabel}`}>
          {managementMode === 'clinical' ? (<>
            {/* Solve split-button: the label ALWAYS names the real window the
                solver will act on (which can exceed the visible week); the
                chevron menu picks the scope, each option showing its dates. */}
            <Dropdown
              align="right"
              disabled={solving}
              splitAction={{
                label: solving ? 'Stop' : 'Solve',
                icon: solving ? Square : Play,
                variant: 'primary',
                onClick: solving ? stopSolving : handleSolveWeekAction,
                className: 'h-[36px] px-4 font-bold text-xs',
              }}
              items={[['weekly', 'Week'], ['biweekly', 'Fortnight'], ['monthly', 'Month']].map(([val, lbl]) => {
                const win = computeSolveWindow ? computeSolveWindow(val) : null;
                return {
                  id: val, label: lbl,
                  checked: solveScope === val,
                  sublabel: win && win.length ? formatDateRange(win[0], win[win.length - 1]) : undefined,
                  onSelect: () => setSolveScope && setSolveScope(val),
                };
              })}
            />
            {onCheckConflicts && (() => {
              // State-coloured check: neutral -> checking -> red (n conflicts)
              // or green (clean). App nulls stale results, so colour = truth.
              const n = conflictCount(rosterCheck);
              const checking = rosterChecking || publishChecking;
              const variant = checking || n === null ? 'secondary' : (n > 0 ? 'danger' : 'success');
              const icon = n === null ? ShieldCheck : (n > 0 ? AlertTriangle : CheckCircle2);
              const text = checking ? 'Checking…' : n === null ? 'Check conflicts' : (n > 0 ? `${n} conflict${n === 1 ? '' : 's'}` : 'No conflicts');
              return (
                <Button onClick={() => { onCheckConflicts(); setShowAudit(true); }} disabled={!schedule || solving || checking} isLoading={rosterChecking} variant={variant} icon={icon}
                  title={n === null
                    ? `Validates the whole solve window (${solveWinLabel}) against every rostering rule`
                    : `Verdict covers the whole window ${solveWinLabel} — it will not change as you page weeks inside it`}
                  className="h-[36px] px-4 font-bold text-xs shadow-none">{text}</Button>
              );
            })()}
            <Button onClick={handlePublishClick} disabled={!schedule || solving || rosterChecking || publishChecking} isLoading={publishChecking} variant="accent" icon={CheckCircle2}
              title="Publishes every day of this window in one action — after a monthly solve there is no need to publish week by week."
              className="h-[36px] px-4 font-bold text-xs shadow-none">Publish</Button>
          </>) : (<>
            {/* Same three controls as clinical, each naming the month it acts on. */}
            <Button onClick={() => startSolvingOnCall(currentMonth.getMonth() + 1, currentMonth.getFullYear())} isLoading={isSolvingOnCall} icon={Play} variant="primary"
              title="Solves every night of this month"
              className="h-[36px] px-4 shadow-none font-bold text-xs">Solve</Button>
            {(() => {
              // Live check of the on-call draft: the same rules a manual
              // assignment is held to, plus leave, blackouts and empty roles.
              const none = !onCallSchedule;
              const variant = none ? 'secondary' : (onCallCritical > 0 ? 'danger' : (onCallIssues > 0 ? 'secondary' : 'success'));
              const icon = none ? ShieldCheck : (onCallCritical > 0 ? AlertTriangle : (onCallIssues > 0 ? ShieldCheck : CheckCircle2));
              const text = none ? 'Check conflicts'
                : onCallCritical > 0 ? `${onCallCritical} conflict${onCallCritical === 1 ? '' : 's'}`
                : onCallIssues > 0 ? `${onCallIssues} warning${onCallIssues === 1 ? '' : 's'}` : 'No conflicts';
              return (
                <Button onClick={() => setShowAudit(true)} disabled={none || isSolvingOnCall} variant={variant} icon={icon}
                  title={`Checks every night of ${onCallWinLabel}: pool, rotation, leave, back-to-back nights, two roles in one night, blackouts and empty roles`}
                  className="h-[36px] px-4 font-bold text-xs shadow-none">{text}</Button>
              );
            })()}
            <Button onClick={() => publishOnCall(onCallSchedule)} disabled={!onCallSchedule || isSolvingOnCall} variant="accent" icon={CheckCircle2}
              title="Publishes every night of this month"
              className="h-[36px] px-4 font-bold text-xs shadow-none">Publish</Button>
          </>)}
            {/* Clear lives in this overflow menu (Oct 2026): it is destructive and
                rarely used, and a full-size button for it pushed the bar onto a
                second row. It still asks before doing anything. */}
            <Dropdown align="right"
              renderTrigger={({ open, toggle }) => (
                <Button variant="secondary" icon={MoreHorizontal} onClick={toggle} aria-label="More actions"
                  aria-expanded={open} aria-haspopup="menu" title="More actions"
                  className="h-[36px] w-[36px] px-0 shadow-none" />
              )}
              items={managementMode === 'clinical'
                ? (onClearWeek ? [{ id: 'clear', label: 'Clear draft', icon: Trash2, danger: true,
                    sublabel: `Removes every assignment and lock in ${solveWinLabel}`,
                    disabled: !schedule || solving, onSelect: () => setConfirmClear(true) }] : [])
                : [{ id: 'clear', label: 'Clear draft', icon: Trash2, danger: true,
                    sublabel: `Removes every on-call duty in ${onCallWinLabel}`,
                    disabled: !onCallSchedule || isSolvingOnCall, onSelect: () => setConfirmClear(true) }]}
            />
          </EndGroup>
        </div>
      </Card>

      <div className="space-y-6 shadow-none">
        {managementMode === 'clinical' ? (
          <div className="space-y-6">
            <div className="bg-white rounded-[20px] shadow-none overflow-x-auto border border-slate-200">
                <table className="w-full border-collapse table-fixed shadow-none">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 shadow-none">
                      <th className="p-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] w-32 border-r border-slate-200 shadow-none">{gridMode === 'location' ? 'Clinic' : 'Staff'}</th>
                      {weekDates.map((date, i) => {
                        const dIso = toLocalISO(date);
                        const closed = closedDates.has(dIso);
                        return (<th key={i} className={`p-3 text-center border-r border-slate-200 last:border-r-0 shadow-none ${closed ? 'bg-rose-50' : (isWeekendDate(date) ? WEEKEND_HEADER : 'bg-white')}`}>
                          <div className="flex items-center justify-center gap-1">
                            <span className={`font-bold text-sm shadow-none ${closed ? 'text-rose-600 line-through' : 'text-slate-800'}`}>{date.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                            {onSetClosedDate && (
                              <button type="button" onClick={() => onSetClosedDate(dIso, !closed)}
                                title={closed ? 'Reopen clinics and OTs on this date' : 'Close all clinics and OTs on this date (public holiday)'}
                                className={`p-0.5 rounded transition-colors ${closed ? 'bg-rose-100 text-rose-600 hover:bg-rose-200' : 'text-slate-300 hover:bg-slate-100 hover:text-slate-500'}`}>
                                <CalendarOff className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <div className={`text-[10px] font-bold shadow-none ${closed ? 'text-rose-400' : 'text-slate-400'}`}>{closed ? 'Closed' : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                        </th>);
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {gridMode === 'location' ? locationCoverage.filter(l => selectedLocation === 'all' || l.location === selectedLocation).map(({ location }) => { const rowCodes = codesForLocation(location); return (<tr key={location} className="border-b border-slate-100 hover:bg-slate-50/30 shadow-none"><td className="p-4 bg-white border-r border-slate-100 font-bold text-slate-700 text-xs truncate shadow-none align-top">{locationName(schedule, location)}</td>{weekDates.map((date, i) => { const ds = toLocalISO(date), dsH = getShiftsForDay(date).filter(s => s.location === location); return (<td key={i} className={`p-1.5 align-top shadow-none border-r border-slate-200 last:border-r-0 ${isWeekendDate(date) ? WEEKEND_CELL : ''}`}><div className="flex flex-col gap-1">{rowCodes.map(code => { const cellShifts = dsH.filter(s => codeOf(s) === code); const isClosed = !openCells.has(`${location}|${ds}|${code}`); return (<div key={code} className="flex items-start gap-1"><StackLabel code={code} /><div className="flex-1 min-w-0">{renderShiftCell(cellShifts, isClosed, date, code)}</div></div>); })}</div></td>); })}</tr>); }) : [...(schedule.employees || [])].sort((a,b) => a.name.localeCompare(b.name)).map(emp => { const rowCodes = codesForEmployee(emp); return (<tr key={emp.id || emp.name} className="border-b border-slate-100 hover:bg-slate-50/30 shadow-none"><td className="p-4 bg-white border-r border-slate-100 font-bold text-slate-700 text-xs truncate shadow-none align-top">{emp.name}</td>{weekDates.map((date, i) => (<td key={i} className={`p-1.5 align-top shadow-none border-r border-slate-200 last:border-r-0 ${isWeekendDate(date) ? WEEKEND_CELL : ''}`}><div className="flex flex-col gap-1">{rowCodes.map(code => { const cellShifts = getShiftsForDay(date).filter(s => samePerson(s.employee, emp) && codeOf(s) === code); const leaveBucket = cellShifts.length ? bucketOf(cellShifts[0]) : (code === 'AM' ? 'AM' : 'PM'); return (<div key={code} className="flex items-start gap-1"><StackLabel code={code} /><div className="flex-1 min-w-0">{renderDocCell(cellShifts, isEmployeeUnavailable(schedule, emp, date, leaveBucket))}</div></div>); })}</div></td>))}</tr>); })}
                  </tbody>
                </table>
              </div>
          </div>
        ) : (
          <div className="space-y-6 shadow-none">
            {onCallSwapSource && <div className="bg-teal-50 border border-teal-100 p-4 rounded-xl flex items-center justify-between animate-pulse shadow-none"><div className="flex items-center gap-3 shadow-none"><ArrowLeftRight className="w-4 h-4 text-teal-600 shadow-none" /><span className="text-xs font-bold text-teal-800 uppercase tracking-widest shadow-none">Swap Mode: Select target cell for {onCallSwapSource.employee?.name || 'Empty'}</span></div><Button size="sm" variant="ghost" onClick={() => setOnCallSwapSource(null)} className="text-teal-600 font-bold uppercase text-[9px] shadow-none">Cancel</Button></div>}
            {onCallView === 'staff' && renderOnCallStaffView()}
            {onCallView === 'roles' && (
            <div className="bg-white rounded-[20px] shadow-none border border-slate-200 overflow-hidden shadow-none"><div><table className="w-full border-collapse table-fixed shadow-none"><thead><tr className="bg-slate-50 border-b border-slate-200 shadow-none"><th className="p-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] w-40 border-r border-slate-200 shadow-none">Date</th>{onCallRoles.map(r => <th key={r.key} className={`p-4 text-center text-[10px] font-bold uppercase tracking-[0.2em] border-r border-slate-200 last:border-r-0 shadow-none ${roleStyle(r.key).includes('orange') ? 'text-orange-600' : 'text-slate-400'}`}>{r.name}</th>)}</tr></thead>                  <tbody className="divide-y divide-slate-100 shadow-none">
                    {onCallMonthDays.map((dateObj, idx) => { 
                      const ds = toLocalISO(dateObj), isW = dateObj.getDay() === 0 || dateObj.getDay() === 6, asgn = getAssignmentsForDay(dateObj); 
                      return (
                        <tr key={idx} className={`group ${isW ? 'bg-[#f8fafc]' : ''} shadow-none`}>
                          <td className={`p-4 border-r border-slate-200 align-middle shadow-none ${isW ? 'bg-[#f8fafc]' : 'bg-white'}`}>
                            <div className="flex flex-col shadow-none">
                              <span className={`text-sm font-bold ${isW ? 'text-terracotta' : 'text-slate-900'} shadow-none`}>{dateObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}</span>
                              <span className="text-[9px] text-slate-400 uppercase font-bold tracking-widest shadow-none">{dateObj.toLocaleDateString('en-GB', { month: 'short' })}</span>
                            </div>
                          </td>
                          {onCallRoles.map(({ key: role }) => { 
                            const s = asgn?.[role], isM = s?.isManual;
                            
                            // Swap Validation (Cell-Level)
                            let swapReason = "";
                            let isCellInvalid = false;

                            if (onCallSwapSource) {
                              const checkS = validateOnCallAssignment(onCallSwapSource.employee, ds, role, onCallSchedule.shifts, schedule, { date: onCallSwapSource.date, role: onCallSwapSource.role });
                              const checkT = s?.employee ? validateOnCallAssignment(s.employee, onCallSwapSource.date, onCallSwapSource.role, onCallSchedule.shifts, schedule, { date: ds, role: role }) : { valid: true };
                              
                              if (!checkS.valid || !checkT.valid) {
                                isCellInvalid = true;
                                const reasons = [];
                                if (!checkS.valid) reasons.push(checkS.reason);
                                if (!checkT.valid) reasons.push(checkT.reason);
                                swapReason = reasons.join(" | ");
                              }
                            }

                            // Define cell background explicitly to avoid artifacts
                            const cellBg = isCellInvalid ? 'bg-rose-100' : (isW ? 'bg-[#f8fafc]' : 'bg-white');

                            return (
                              <td key={role} 
                                  onClick={() => isCellInvalid ? null : (onCallSwapSource ? handleOnCallSwapExecute(s || { date: ds, role }) : setEditingOnCallShift(s || { date: ds, role }))} 
                                  className={`p-3 border-r border-slate-100 last:border-r-0 shadow-none relative group/cell ${cellBg} ${isCellInvalid ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                  style={isCellInvalid ? {backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(225,29,72,0.2) 5px, rgba(225,29,72,0.2) 10px)'} : {}}
                              >
                                {isCellInvalid && swapReason && onCallSwapSource && (
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2.5 bg-[#042f2e] text-white text-[10px] font-bold rounded-lg shadow-xl opacity-0 group-hover/cell:opacity-100 pointer-events-none transition-all z-[100] text-center leading-relaxed">
                                    {swapReason}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-[#042f2e]" />
                                  </div>
                                )}

                                {s?.employee ? (
                                  <div className={`flex items-center justify-center h-10 bg-white border border-slate-200 rounded-lg text-slate-700 font-bold text-xs shadow-none relative hover:scale-[1.02] transition-all ${isM ? 'ring-1 ring-terracotta/20' : ''}`}>
                                    <span className="relative z-10">{s.employee.name}</span>
                                    {isM && <div className="absolute -top-1 -right-1 p-0.5 bg-white rounded-full shadow-sm border border-slate-100 shadow-none z-20"><User className="w-2.5 h-2.5 text-terracotta" /></div>}
                                  </div>
                                ) : <div className={`h-10 rounded-lg border border-dashed border-slate-200 ${isW ? 'bg-slate-100/30' : 'bg-slate-50/30'} hover:border-teal-300 shadow-none`}></div>}
                              </td>
                            ); 
                          })}
                        </tr>
                      ); 
                    })}
                  </tbody></table></div></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;
export { validateOnCallAssignment };
